import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { SHORTCUT } from '../utils/platform';
import { useReminderCapture } from '../hooks/useReminderCapture';
import { isDesignatedReminderList, isDesignatedLogList, getListEmptyHint } from '../hooks/useUserPrefs';
import { applyListSizeGuard } from '../lib/listLimits';
import { useEditLock } from '../hooks/useEditLock';
import { useIsOnline } from '../hooks/useIsOnline';
import { useStickyEditing } from '../hooks/useStickyEditing';
import { hasConflictMarker } from '../lib/mergeConflict';
import ShareBadge from './ShareBadge';
import GuestReminderBand from './GuestReminderBand';
import SyntaxBackdrop from './SyntaxBackdrop';
import { createSerialRunner } from '../lib/serialSave';
import { shouldAdvanceBase } from '../lib/baseAdvance';
import { flagKey, isConflictBandVisible } from '../lib/conflictBand';
import HighlightPopup from './HighlightPopup';
import useLineHighlight from '../hooks/useLineHighlight';
import { setFormatTarget, blurFormatTarget } from '../lib/formatTarget';
import HistoryPanel from './HistoryPanel';
import PortalMenu from './PortalMenu';
import { SubMenu } from './MenuKit';
import { parseOutline, parseDateAnchors, mergeOutlineAndDates, formatDateAnchorLabel, jumpToOffset } from '../lib/outline';
import { stripRecurrenceGlyph } from '../lib/formatBellLine';
// Merge-on-receive (Walter 2026-08-07): adoption-time 3-way merge + the
// write-ahead archive of a diverged local buffer. See the sync effect below.
import { decideAdoption } from '../lib/mergeOnReceive';
import { getClientId } from '../lib/clientId';
import { logSync, fingerprint } from '../lib/syncLog';
import { recordContent } from '../lib/contentTrail';
import { noteShrink, removedLines, countLines } from '../lib/shrinkLog';
import { archiveDivergedLocal, archivePreMerge } from '../lib/historyConflict';
import { moveLinesUp, moveLinesDown } from '../lib/moveLines';
import { LIST_COLORS, getListColorValue, getListColorTextValue, getListColorTint, getListColorHeaderBg, getListColorBodyBg } from '../lib/listColors';
import { createDragAutoScroll, nearestScrollable } from '../lib/dragAutoScroll';
import MarkdownEditor, { dispatchDocReplace } from './MarkdownEditor';
import { isLivePreviewEditorActive } from '../lib/editorFlag';

const MIN_W = 180;
const MIN_H = 70;
// Walter 2026-05-21: default tile dimensions. The "reset size"
// header button snaps a free-canvas tile back to these — same values
// BoardPage seeds new lists with.
const DEFAULT_W = 280;
const DEFAULT_H = 320;

export default function ListPanel({
  list, onUpdate, onClose, onArchive, onUnarchive, onDelete,
  onFocus, onExpand, isFocused, onSaveStatus,
  // Walter 2026-05-15: optional. When provided, ListPanel registers
  // its flushSave with the parent on mount so the header
  // SaveIndicator's tap-to-force-sync gesture can flush this list
  // along with any others. Returns an unregister fn.
  registerPendingFlush,
  boards = [], currentBoardId, onMoveList,
  // Reminder capture wiring — only present when caller provides them.
  // ListPanel still works without these (in which case the banner just
  // never fires).
  userPrefs, updateUserPrefs, createReminder,
  reminders, updateReminder, deleteReminder, pushToast,
  // Walter 2026-05-06 round 5: when set, clicking the
  // designated-reminder-list count band opens the structured
  // reminder view (agenda panel). Wired from BoardPage's
  // setShowAgenda(true).
  onOpenAgenda,
  // Walter 2026-05-11: when the user clicks on the 🔁 glyph next to a
  // 🔔 in a list, open the structured edit popover for that reminder.
  // Wired from BoardPage; null-safe (no-op if not provided).
  onOpenReminderEditor,
  // Walter 2026-05-12: + button in the designated reminder list's
  // header opens the structured-reminder popover in create mode.
  // Receives (list, anchorRect) so the parent can position the
  // popover and know which source list to append to on save.
  onRequestNewReminder,
  jumpTarget, onJumpHandled,
  // Grid mode: the tile's outer box fills its parent slot (which is sized
  // by GridCanvas) rather than using list.x/y/w/h. Header is no longer a
  // drag handle, and the resize nub is hidden — both are slot-managed in
  // grid mode.
  inGrid = false,
  // Grid mode drag handshake: when present, a header press starts a
  // grid reorder drag (handled entirely by GridCanvas). Only meaningful
  // with inGrid=true.
  onGridHeaderMouseDown,
  // Sharing/collab — alpha. When the list is on a shared board, we
  // engage the soft-lock primitive (useEditLock). For solo lists or
  // when the alpha flag is off, the lock is a no-op (canEdit always
  // true). `currentUid` and `currentUserName` are the user editing
  // right now; `boardOwnerUid` is the doc-path uid (= currentUid for
  // own boards; = inviter for shared boards once Phase 2 lands).
  currentUid,
  currentUserName,
  // Walter 2026-05-28: true when current user is signed in as a
  // guest (anonymous). Drives the reminder-list visual hint — bell-
  // line text in a designated reminder list renders dim so the user
  // gets an ambient signal that those reminders won't actually fire
  // until they upgrade to a real account (reminders require email or
  // push delivery, both gated on auth).
  isGuest = false,
  // Walter 2026-05-28: opens the upgrade flow (typically the
  // AccountPopover's "sign in to keep your work safe" path). Wired
  // from BoardPage. The GuestReminderBand's "sign in" link calls it.
  onRequestSignIn,
  boardOwnerUid,
  boardEditors = [],
  boardViewers = [],
  // Open the Share modal for this list. Null when sharing alpha is
  // off — the menu item then doesn't render at all.
  onShareList,
  // Peek share state on this list (always wired). Backs the inline
  // ShareBadge in the tile header — owner sees full management,
  // guest sees viewOnly participants peek.
  onPeekShare,
  // Walter 2026-05-11: open the board-picker so the user can place
  // this shared list inside one of their own boards. Wired from
  // BoardPage; visibility gated on `isSharedWithMe` below.
  onPlaceList,
  // True when this list is shared with the current user (they're
  // not the owner). Drives "place in board…" + per-user delete
  // semantics in the menu.
  isSharedWithMe = false,
  // Walter 2026-05-13: organize-reminders Accept/Undo flow.
  // onTriggerOrganize() is called when the user clicks the
  // sort-button in the tile header — the actual organize runs in
  // BoardPage so the same flow can fire from the agenda toolbar.
  // Walter 2026-07-04: organize now applies directly and offers an
  // undo toast (the old review/accept band was removed — the sort is
  // reliable enough that the extra confirmation step was just friction).
  onTriggerOrganize,
}) {
  const panelRef = useRef(null);
  const titleInputRef = useRef(null);
  const textareaRef = useRef(null);
  const mdEditorRef = useRef(null);
  // Experimental live-preview (CodeMirror-6) editor, opt-in per device. Read
  // once at render — toggling it in Settings reloads the page (see editorFlag).
  const useLivePreview = isLivePreviewEditorActive();
  const dragStart = useRef(null);
  const resizeStart = useRef(null);
  const saveTimer = useRef(null);
  // Walter 2026-07-04: when the user clicks into a list that's locked by
  // someone else, plain typing does nothing — which was puzzling. Briefly
  // pulse the "take over" button so the way forward is obvious.
  const [takeoverFlash, setTakeoverFlash] = useState(false);
  const takeoverFlashTimer = useRef(null);
  useEffect(() => () => { if (takeoverFlashTimer.current) clearTimeout(takeoverFlashTimer.current); }, []);
  const flashTakeover = () => {
    setTakeoverFlash(true);
    if (takeoverFlashTimer.current) clearTimeout(takeoverFlashTimer.current);
    takeoverFlashTimer.current = setTimeout(() => setTakeoverFlash(false), 1400);
  };
  const pendingPatch = useRef(null); // merged patch waiting to be flushed
  const isDragging = useRef(false);
  const menuRef = useRef(null);

  const [localContent, setLocalContent] = useState(list.content || '');
  // Walter 2026-06-15: 3-way conflict BASE — the RAW cloud content this
  // editor's buffer currently descends from. Captured here (editor-side),
  // NOT from the timestamp baseline in useData, because that baseline
  // advances on every server snapshot even while this buffer is held on
  // the user's in-progress edit. Updated when we ADOPT a server value
  // (reconciliation effect) and after a successful SAVE (flushSave). Sent
  // to the write via the reserved `__baseContent` patch key so the cloud
  // write can detect a silent divergence. Must be the raw (un-stripped)
  // value so the display-only recurrence-glyph strip never reads as a diff.
  const baseContentRef = useRef(list.content || '');
  // Walter 2026-09-08: the versions this panel has held, oldest first, so the
  // adoption decision can tell an incoming version it has ALREADY moved past
  // from genuine news. Without it, a snapshot that lost a race with our own
  // save is adopted verbatim and the line we just typed is gone — his
  // reproduction, and the row that proved it, are in lib/mergeOnReceive's
  // isStaleEcho. Every assignment to baseContentRef goes through setBase so
  // the two can't drift; that wiring is the part worth being careful about.
  const versionHistoryRef = useRef([]);
  // Walter 2026-10-31: texts THIS WINDOW has written. `lastWriteByClientId`
  // identifies the DEVICE, and he runs two windows on one PC - so on its own
  // it would brand the other window's deliberate undo as our own echo and
  // ignore it, trading one kind of disagreement for another. Combined with
  // this set, the echo guard (#250) fires only for text we actually wrote.
  const wroteRef = useRef(new Set());
  const noteWritten = (text) => {
    const s = String(text ?? '');
    wroteRef.current.add(s);
    if (wroteRef.current.size > 8) wroteRef.current.delete(wroteRef.current.values().next().value);
  };

  const setBase = useCallback((text) => {
    const t = String(text ?? '');
    baseContentRef.current = t;
    const h = versionHistoryRef.current;
    if (h.length === 0 || h[h.length - 1].text !== t) {
      h.push({ text: t, at: Date.now() });
      // A handful is plenty: this only has to span the seconds between a
      // write and a snapshot that lost the race with it.
      if (h.length > 8) h.shift();
    }
  }, []);
  // Walter 2026-08-07 (merge-on-receive): a ref mirror of localContent. The
  // snapshot-sync effect has to know what the buffer currently holds to tell
  // "adopt" from "we both moved" — but taking `localContent` as a dependency
  // would re-run that effect on every keystroke. Read-only inside the effect.
  // Same pattern ListPage already uses.
  const localContentRef = useRef(list.content || '');
  useEffect(() => { localContentRef.current = localContent; }, [localContent]);
  // Which list the sync effect last reconciled. Tiles are keyed by list.id so
  // this instance shouldn't be reused across lists — but merging one list's
  // buffer into another would be catastrophic, so the guard is cheap
  // insurance (and FocusedPanel, which IS reused on mobile, needs it).
  const syncedListIdRef = useRef(list.id);
  const [localTitle, setLocalTitle] = useState(list.title || '');
  // Walter 2026-05-16: history-panel modal state. Opens when the user
  // clicks the ↶ glyph next to the title (only shown when this list
  // has at least one history entry — list.historyCount > 0).
  const [showHistory, setShowHistory] = useState(false);
  // Walter 2026-09-14 (fix 2): the conflict flag this panel just resolved.
  // "Mark resolved" clears it in one atomic write, but this panel only hears
  // about that write through its normal snapshot, which can trail by seconds,
  // so the band used to linger and appear to come back. Remembering the exact
  // flag hides it at once, while a genuinely new conflict (a new
  // serverTimestamp) still shows. lib/conflictBand.
  const [dismissedConflictKey, setDismissedConflictKey] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Re-runs the snapshot-sync effect after a refused-while-typing snapshot
  // (Walter 2026-07-19 two-PC divergence — see the effect's comment).
  const [syncRetryTick, setSyncRetryTick] = useState(0);

  // Edit-lock: engage whenever the BOARD is shared (has at least one
  // collaborator) or the LIST itself has its own ACL. Solo lists
  // short-circuit to canEdit=true without doing any Firestore writes.
  //
  // Importantly, this is NOT gated behind the local user's alpha flag.
  // The alpha flag controls who sees the share UI (only opted-in users
  // can invite); but the lock has to engage uniformly across all users
  // touching a shared list, otherwise an invitee who hasn't opted into
  // alpha would silently bypass the lock and let two people edit at
  // once — the very thing soft-lock is meant to prevent.
  // Walter 2026-05-12: require an OTHER user (not just currentUid) in
  // at least one ACL array. New lists created on a board that has any
  // share entries inherit those entries verbatim (BoardPage.js,
  // 2026-05-11 inheritance code); if a board was ever self-shared OR
  // an editor was removed without rewriting per-list editors, a
  // freshly-created list ends up with editors=[currentUid] and
  // lockEnabled fires, surfacing "editing shared list" + acquiring
  // the soft-lock on writes that no one else can see. Filtering
  // currentUid out before the length check kills that false-positive
  // entirely — a lock is only meaningful when at least one OTHER
  // person could be editing.
  const hasOtherCollaborator = (uids) =>
    Array.isArray(uids) && uids.some(u => u && u !== currentUid);
  // Walter 2026-05-16: lock is now ALWAYS on (was previously gated to
  // shared lists only). Per-device clientId discrimination in
  // useEditLock means same-uid different-device = different holder,
  // so the lock prevents the multi-device stale-base last-write-wins
  // problem too. The `hasSharedCollaborator` flag is still useful to
  // know elsewhere (e.g. ACL banners), so kept as a derived value.
  const hasSharedCollaborator = !!(
    hasOtherCollaborator(boardEditors) ||
    hasOtherCollaborator(boardViewers) ||
    hasOtherCollaborator(list?.editors) ||
    hasOtherCollaborator(list?.viewers)
  );
  const lockEnabled = true;
  // Walter 2026-05-11: for placed shared lists the lock doc lives at
  // the SOURCE path (under the owner's subtree) — that's where the
  // actual list doc + its editLock field live. Using the local
  // (boardOwnerUid + currentBoardId) tuple here writes the lock to
  // the invitee's own empty subtree, where it can't be seen by other
  // collaborators and where snap.exists() returns false on every
  // acquire — so acquires silently no-op and two users can edit
  // simultaneously. Prefer the placement-annotated path when present.
  const lockOwnerUid = list?._sharedFromOwnerUid || boardOwnerUid || currentUid;
  const lockBoardId = list?._sharedFromBoardId || currentBoardId;
  const lock = useEditLock({
    ownerUid: lockOwnerUid,
    boardId: lockBoardId,
    list,
    currentUid,
    currentUserName,
    enabled: lockEnabled,
    // Surface "Tom took over editing" as a regular toast — it's our
    // overlay-banner-with-auto-dismiss surface. The displaced user
    // gets a clear notification that their lock was force-grabbed.
    onKicked: (byName) => {
      pushToast?.(`${byName} took over editing "${list?.title?.trim() || 'this list'}"`, null, { ttlMs: 5000 });
    },
  });

  // Role-based write gate. Independent of the soft-lock: even if no
  // one else holds the lock, viewers must be read-only. Three ways
  // to qualify as an editor:
  //   1. You ARE the owner (boardOwnerUid === currentUid).
  //   2. You're in the parent board's `editors` array.
  //   3. You're in THIS list's `editors` array (per-list share).
  // Anyone else (including users in `viewers` arrays at any level)
  // is read-only. This mirrors the firestore.rules write gates so
  // the UI prevents the action that would otherwise silently fail
  // with a permission-denied at the server.
  const roleCanEdit = useMemo(() => {
    if (!currentUid) return false;
    if (boardOwnerUid && boardOwnerUid === currentUid) return true;
    if (Array.isArray(boardEditors) && boardEditors.includes(currentUid)) return true;
    if (Array.isArray(list?.editors) && list.editors.includes(currentUid)) return true;
    return false;
  }, [currentUid, boardOwnerUid, boardEditors, list?.editors]);

  // Connectivity gate: shared lists become read-only when the tab is
  // offline. Walter's decision 2026-05-04 — solo writers' offline
  // edits are fine (Firestore queues + syncs), but two users on a
  // shared list can't both edit safely without the soft-lock, and
  // the soft-lock requires a Firestore round-trip to acquire (fails
  // offline). Easier to block writes entirely than to handle the
  // conflict-resolution mess. Own lists (lockEnabled=false) stay
  // editable.
  const isOnline = useIsOnline();
  // Walter 2026-05-16: offlineLockedShared is "shared list while
  // offline = read-only" — designed to keep two users from making
  // diverging edits offline that then conflict on reconnect. NOT
  // applicable to solo lists; the user can edit their own list
  // offline and Firestore queues the writes. Gate on
  // hasSharedCollaborator now that the lock itself is always-on.
  const offlineLockedShared = hasSharedCollaborator && !isOnline;

  // Effective gate: role must allow, lock must allow when engaged,
  // AND we must be online for shared lists.
  const effectiveReadOnly = !roleCanEdit
    || (lockEnabled && !lock.canEdit)
    || offlineLockedShared;

  // Sticky "actively editing" — Walter's s2b fix (round 6): the
  // "editing shared list" band primarily watches lock.heldByMe, but
  // on real mobile that signal flickers when Firestore snapshots
  // lapse during page-hide / spotty network. We layer a local-state
  // OR so as long as the user has typed in the last 10s the band
  // stays visible, even if the lock snapshot blinks.
  const [isStickyEditing, markEditing] = useStickyEditing(10000);

  // Reminder capture: wires the textarea up to the inline-banner flow.
  // Confirms create a Firestore reminder doc + prepend "🔔 " to the line
  // in the source content.
  const reminderCapture = useReminderCapture({
    list,
    boardId: currentBoardId,
    // Bug #12 — capture flow needs currentUid to build aclUids on
    // each createReminder call (the source list's editors + viewers
    // + owner). See useReminders.computeAclForCreate.
    currentUid,
    userPrefs,
    updateUserPrefs,
    createReminder,
    updateReminder,
    reminders,
    deleteReminder,
    pushToast,
    // Range-replace mutator: replace [from, to) in localContent with
    // `insert`. Used both for the cross-list capture confirm flow
    // (where we replace the whole line with 🔔 + resolved-date variant)
    // and for the undo (which replaces the new line with the original).
    onContentMutate: ({ from, to, insert }) => {
      // CM6 (live-preview) owns the document — apply the edit to the view so the
      // caret maps and we don't fight the localContent mirror. Walter
      // 2026-07-13: fixes the missing 🔔 / deleted line on Enter-capture.
      const cmView = mdEditorRef.current?.view;
      if (useLivePreview && cmView) {
        const docLen = cmView.state.doc.length;
        const safeTo = Math.max(0, Math.min(to, docLen));
        const safeFrom = Math.max(0, Math.min(from, safeTo));
        cmView.dispatch({ changes: { from: safeFrom, to: safeTo, insert } });
        return;
      }
      setLocalContent(prev => {
        const next = prev.slice(0, from) + insert + prev.slice(to);
        scheduleSave({ content: next });
        // Cursor adjust: push by the net length change. Behaviour is
        // forgiving — if the cursor was inside the replaced range, we
        // park it at the end of the inserted text; outside, we shift
        // to keep its absolute position relative to surrounding text.
        const delta = insert.length - (to - from);
        const ta = textareaRef.current;
        if (ta) {
          const cur = ta.selectionStart || 0;
          let newCur;
          if (cur < from) newCur = cur;
          else if (cur > to) newCur = cur + delta;
          else newCur = from + insert.length;
          requestAnimationFrame(() => {
            try { ta.setSelectionRange(newCur, newCur); } catch {}
          });
        }
        return next;
      });
    },
  });
  // Walter 2026-07-02: move + colour are now hover fly-outs (SubMenu), so the
  // old menuMode replace-in-place state is gone.
  const otherBoards = useMemo(
    () => (boards || []).filter(b => b.id !== currentBoardId),
    [boards, currentBoardId],
  );

  // Walter 2026-05-17: dismiss is now handled inside <PortalMenu>
  // (pointerdown + touchstart in capture phase). The menuMode reset
  // on close is already covered by the useEffect a few lines up
  // (line ~273) so this comment is the only marker of the migration.

  useEffect(() => {
    // Walter 2026-05-12: legacy "🔔↻" prefixes from the 2026-05-11
    // picker round read as cryptic — drop the ↻ glyph on read so
    // older bell lines clean up. Same shape as the previous
    // backfill effect.
    //
    // Walter 2026-05-16: bail guard now requires focus AND recent
    // typing (within ~10s, tracked by isStickyEditing). The earlier
    // "focus alone" check meant the textarea ALSO blocked remote
    // content updates when the user had focused the textarea but
    // wasn't actively typing — which broke the multi-device
    // take-over case. After taking over the lock from another
    // device, the user focuses the textarea to start editing, but
    // the just-arrived remote content from the other device was
    // refused entry, so they saw stale content. With the recent-
    // typing gate, the effect allows snapshot-driven updates when
    // the user is idle even with focus, but still protects against
    // cursor-jumping mid-sentence.
    const ta = textareaRef.current;
    // Bail while ACTIVELY typing (focus + keystroke within 10s) — but a
    // refused snapshot MUST be retried once the typing window closes
    // (Walter 2026-07-19 two-PC divergence: office edits arrived while he
    // typed at home → refused → NEVER retried → home's base went stale →
    // its next save merged against ancient state and dropped lines; home
    // + phone then sat silently diverged. The June sync doc called this
    // exact gap: "the remote snapshot is refused and never retried".)
    // The retry tick re-runs this effect ~1s after the sticky window can
    // have expired; if still typing it just re-arms. Save-debounce (4s)
    // beats the retry (11s), so local edits are flushed and echoed back
    // before the retry adopts — nothing unsaved gets clobbered.
    const activelyTyping = isStickyEditing
      && ((ta && document.activeElement === ta) || mdEditorRef.current?.view?.hasFocus);
    if (activelyTyping) {
      const t = setTimeout(() => setSyncRetryTick(x => x + 1), 11000);
      return () => clearTimeout(t);
    }
    const raw = list.content || '';
    const stripped = stripRecurrenceGlyph(raw, list.id, reminders);
    // ── MERGE ON RECEIVE (Walter 2026-08-07; plan: docs/proposals/
    //    2026-07-19-sync-hardening-merge-on-receive.md) ──
    // Overwriting the buffer with the server value is right when only ONE
    // side moved. When the server moved AND our buffer has diverged
    // (unflushed offline edits, a save that never landed, simultaneous
    // typing that both clean-committed), the overwrite discards the local
    // edits with no snapshot — the last silent data-loss path, and one the
    // write path structurally cannot see. So ASK first (pure decision in
    // lib/mergeOnReceive.js, which defers to the untouched threeWayMerge),
    // then apply. Decided in "stripped space" — the text the user sees and
    // the value a save would write — so the display-only recurrence-glyph
    // strip can never read as a divergence.
    const baseStripped = stripRecurrenceGlyph(baseContentRef.current, list.id, reminders);
    // Read-only lists (viewer role, another device holding the lock, shared-
    // list-while-offline) keep the pre-existing plain adopt: we must not
    // write back a merge, and a read-only buffer can't have diverged anyway.
    // Same for the first snapshot after this panel switched lists.
    const switchedList = syncedListIdRef.current !== list.id;
    syncedListIdRef.current = list.id;
    const decision = (effectiveReadOnly || switchedList)
      ? { action: 'adopt', content: stripped }
      : decideAdoption({
        base: baseStripped,
        local: localContentRef.current,
        server: stripped,
        history: versionHistoryRef.current,
        now: Date.now(),
        // Walter 2026-10-12 (#250): our own write coming back is an echo
        // however long it took. See lib/mergeOnReceive.isStaleEcho.
        fromSelf: list.lastWriteByClientId === getClientId() && wroteRef.current.has(stripped),
      });

    // Walter 2026-08-24: RECORD THE DECISION. The log had saves and
    // snapshots but not the adopt/merge/conflict choice itself, so when his
    // Reminders list banded on a single online device the log couldn't say
    // which branch fired or what the three versions looked like — it took
    // screenshots of the history modal to reconstruct. Sizes + fingerprints
    // only, same as every other line: never content.
    // Two cases are pure noise and stay out: `keep` (the server didn't
    // move) and an `adopt` whose server value we already hold — the echo of
    // our own save, which happens after every write.
    const boring = decision.action === 'keep'
      || (decision.action === 'adopt' && localContentRef.current === stripped);
    if (!boring) {
      logSync('adopt-decision', {
        list: list.id,
        action: decision.action,
        base: `${baseStripped.length}/${fingerprint(baseStripped)}`,
        local: `${localContentRef.current.length}/${fingerprint(localContentRef.current)}`,
        server: `${stripped.length}/${fingerprint(stripped)}`,
        ...(decision.conflicts ? { clashes: decision.conflicts } : {}),
      });
    }

    if (decision.action === 'stale') {
      // The incoming version is one we already moved past — a snapshot that
      // lost the race with our own write. Change NOTHING, and in particular
      // do not advance base: base still describes the version our buffer
      // descends from, and moving it to this older text would hand the next
      // decision a false ancestor. The server catches up on our next save.
      // Walter 2026-09-08 — this is the branch that was deleting his lines.
      return;
    }

    if (decision.action === 'keep') {
      // Server unchanged — adopting would only discard unsaved local edits
      // (Walter 2026-08-22: a line typed and not yet saved disappeared here).
      // Base still tracks the server's exact string.
      setBase(raw);
      return;
    }

    if (decision.action === 'merge') {
      // Clean 3-way — converge silently, keeping BOTH sides' edits. Base
      // advances to the server value we merged against, so the write-back's
      // conflict check sees theirs === base: no band, the merge lands, and
      // the other device adopts it through its own merge-on-receive. That's
      // the convergence loop, and it terminates — adoption never writes, so
      // once both sides agree nothing further is emitted.
      // Walter 2026-09-03 (content-writer audit): a clean merge is applied
      // silently and used to leave NOTHING behind. "Clean" means the algorithm
      // saw no overlapping edits, not that it got it right — and it reads a
      // moved block as delete+insert (#168), so a misjudged merge dropped
      // lines with no band and no snapshot. One small write buys the pre-merge
      // text back. Fire-and-forget: the net must never delay the merge.
      archivePreMerge({
        uid: lockOwnerUid,
        boardId: lockBoardId,
        listId: list.id,
        content: stripped,
        baseContent: baseStripped,
        actorUid: currentUid,
        source: 'merge-on-receive',
      });
      noteShrink({
        listId: list.id,
        reason: 'merge',
        removed: removedLines(localContentRef.current, decision.content),
        beforeLines: countLines(localContentRef.current),
        afterLines: countLines(decision.content),
      });
      recordContent(list.id, decision.content, 'merge');
      setLocalContent(decision.content);
      setBase(raw);
      scheduleSave({ content: decision.content });
      pushToast?.('merged edits from another device', null, { ttlMs: 5000 });
      return;
    }

    if (decision.action === 'conflict') {
      // Genuine same-line clash. WRITE-AHEAD BACKSTOP: archive our diverged
      // buffer to history + raise the band FIRST, and only adopt the server
      // value once that has actually landed. If the archive fails (offline,
      // rules, list deleted) we keep the buffer instead of dropping it — the
      // divergence then stays visible to the write path, whose own conflict
      // detection snapshots on the next save. Never trade the user's text
      // for a round-trip we didn't confirm.
      let retry = null;
      let cancelled = false;
      archiveDivergedLocal({
        uid: lockOwnerUid,
        boardId: lockBoardId,
        listId: list.id,
        content: decision.divergedLocal,
        baseContent: baseStripped,
        actorUid: currentUid,
      }).then(({ historyId }) => {
        if (cancelled) return;
        // The user started typing during the round-trip — their keystrokes
        // outrank this adoption (and adopting now would jump the caret).
        // Re-arm instead; the buffer we archived is safe either way.
        const stillSameBuffer = localContentRef.current === decision.divergedLocal;
        if (!historyId || !stillSameBuffer) {
          retry = setTimeout(() => setSyncRetryTick(x => x + 1), 11000);
          return;
        }
        setLocalContent(stripped);
        setBase(raw);
      }).catch((e) => {
        console.warn('[merge-on-receive] archiving the diverged buffer failed (keeping it)', e);
        if (cancelled) return;
        retry = setTimeout(() => setSyncRetryTick(x => x + 1), 11000);
      });
      return () => { cancelled = true; if (retry) clearTimeout(retry); };
    }

    // Walter 2026-08-31: the buffer is about to change without him typing —
    // exactly the shape of the loss he reported. Keep the text while the
    // trail is on (lib/contentTrail).
    // An incoming change that REMOVES lines is the event we're hunting: the
    // user didn't delete them, this window just received a version without
    // them. Always on and long-retained (lib/shrinkLog) — the content trail
    // rolls over in hours and his losses are days apart.
    noteShrink({
      listId: list.id,
      reason: 'adopt',
      removed: removedLines(localContentRef.current, decision.content),
      beforeLines: countLines(localContentRef.current),
      afterLines: countLines(decision.content),
    });
    recordContent(list.id, decision.content, 'adopt');
    setLocalContent(decision.content);
    // We just ADOPTED the server's content — our buffer now descends from
    // it, so it becomes the 3-way conflict base. (Guarded above: if we're
    // mid-edit we return before here, leaving base on the pre-edit value so
    // a concurrent remote write is still detected as a conflict on save.)
    setBase(raw);
    // Walter 2026-05-18: removed the auto-`scheduleSave` here that
    // wrote the stripped content back to Firestore. The problem:
    // when the listener delivers a STALE cached version on reconnect,
    // the strip would fire against that stale content and the resulting
    // save would race the cloud's newer state, tripping a false
    // conflict (B-mechanism flagged it because baseline < server
    // updatedAt). Walter caught this — "phone went offline briefly,
    // came back, two lists showed conflicts even though I made no
    // edits." Display-side strip stays so the user never sees the
    // legacy glyphs; the cloud copy migrates organically whenever
    // the user makes a real edit, since edits start from the
    // stripped localContent. Lists that are never edited stay
    // "dirty" on the cloud forever, but the user can't see it.
    // eslint-disable-next-line
  }, [list.content, list.id, reminders, syncRetryTick]);
  // Walter 2026-05-18: sync localTitle from server only when list.title
  // actually CHANGES (server-side echo), not on every editingTitle
  // transition. Before this fix: blurring a freshly-typed title flipped
  // editingTitle false → effect ran → list.title was still the OLD value
  // (server hadn't echoed yet) → localTitle got reset to the old value
  // → user saw their text disappear. Reading editingTitle via a ref so
  // it isn't in the dep array.
  const editingTitleRef = useRef(editingTitle);
  useEffect(() => { editingTitleRef.current = editingTitle; }, [editingTitle]);
  useEffect(() => {
    if (editingTitleRef.current) return;
    setLocalTitle(list.title || '');
  }, [list.title]);

  // Expand this panel into focused view, carrying the current cursor position across.
  const expandWithCursor = useCallback(() => {
    const offset = textareaRef.current?.selectionStart ?? 0;
    onExpand?.(list.id, offset);
  }, [list.id, onExpand]);

  // Ctrl+E on the focused panel toggles into focused view.
  useEffect(() => {
    if (!isFocused) return;
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        if (document.activeElement === textareaRef.current) {
          e.preventDefault();
          expandWithCursor();
        }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isFocused, expandWithCursor]);

  // Jump to char offset when triggered from search.
  // Walter 2026-05-13: defer to next frame — see the matching comment
  // in FocusedPanel for the rationale (textarea clientHeight is 0 on
  // initial mount, scroll-to-center math produces scrollTop=0 and the
  // entry lands off-screen even though the caret is at the right
  // offset).
  useEffect(() => {
    if (!jumpTarget) return;
    const id = requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      const offset = jumpTarget.charOffset ?? 0;
      ta.focus();
      ta.setSelectionRange(offset, offset);
      const lineHeight = parseInt(getComputedStyle(ta).lineHeight) || 20;
      const lineNumber = (localContent.slice(0, offset).match(/\n/g) || []).length;
      ta.scrollTop = Math.max(0, lineNumber * lineHeight - ta.clientHeight / 2);
      // Walter 2026-05-12: bring the tile itself into the window
      // viewport. Two passes — immediate auto-scroll (Safari ignores
      // smooth during focus flow), then a delayed re-check after the
      // popover close animation and any layout settle has finished.
      // scrollIntoView from the textarea targets the nearest
      // scrollable ancestor; we walk up to the tile's outer container
      // (the closest [data-list-tile] element) to scroll from there,
      // which handles the case where the textarea is short-and-
      // already-visible-internally but the TILE is below the fold.
      const tile = ta.closest('[data-list-tile]') || ta;
      tile.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
      // Second pass after a brief delay — the agenda popover's
      // dismiss animation runs ~120ms and can shift the layout. A
      // 200ms re-call catches the final position.
      setTimeout(() => {
        try {
          tile.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
        } catch (_) { /* element may be unmounted by now — no-op */ }
      }, 200);
      onJumpHandled?.();
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line
  }, [jumpTarget]);

  // Debounced save with explicit flush.
  // Walter 2026-05-15: report 'syncing' just before the actual write
  // fires (Firestore round-trip starts here, however brief). The
  // SaveIndicator uses this to show the pulsing-floppy "syncing" state
  // distinct from the "pending / counting down" state.
  //
  // Walter 2026-09-14 (fix 2): saves run STRICTLY ONE AFTER ANOTHER. His
  // 2026-09-13 conflict band was his phone racing its own saves: a save sent
  // while the previous one was still in flight carried the base from BEFORE
  // that previous save (the base below only advances once a write returns),
  // so the three-way merge read his own consecutive edits to one line as a
  // same-line clash. flushSave fires from the 4s debounce, visibilitychange,
  // pagehide, the force-sync tap, unmount and explicit commits, and nothing
  // used to make a second call wait. Now each save queues behind the last,
  // and reads pendingPatch and the base when it STARTS — after the previous
  // save has advanced it. What a save writes is unchanged. lib/serialSave.
  //
  // Considered: on pagehide during an in-flight save, a queued save now waits
  // rather than starting at once. Unload cancels in-flight requests either
  // way, and the save it would have sent was the stale-base one that caused
  // the band — so no real loss of protection.
  const saveRunnerRef = useRef(null);
  if (!saveRunnerRef.current) saveRunnerRef.current = createSerialRunner();
  const flushSave = useCallback(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    return saveRunnerRef.current(async () => {
      if (!pendingPatch.current) return;
      const patch = pendingPatch.current;
      pendingPatch.current = null;
      if (onSaveStatus) onSaveStatus('syncing');
      // Tag content writes with the 3-way conflict base (stripped centrally
      // in saveList before it reaches Firestore). Without `content`, no base.
      // Read HERE, at start, so a queued save sees the previous save's base.
      const baseAtStart = baseContentRef.current;
      const outgoing = ('content' in patch)
        ? { ...patch, __baseContent: baseAtStart }
        : patch;
      await onUpdate(list.id, outgoing);
      // The cloud now holds what we just wrote — advance the base so the
      // next edit compares against our own write, not the pre-write value.
      // Walter 2026-09-19 (#238): UNLESS an adoption moved the base while we
      // were writing. Then the base we would set is the OLDER text and lands
      // behind the buffer — a false ancestor, which his 21:09 log caught
      // banding two of his own consecutive edits. See lib/baseAdvance.
      if ("content" in patch && shouldAdvanceBase(baseAtStart, baseContentRef.current)) {
        setBase(patch.content ?? "");
        noteWritten(patch.content ?? "");
      }
      if (onSaveStatus) onSaveStatus('saved');
    });
  }, [list.id, onUpdate, onSaveStatus]);

  const scheduleSave = useCallback((patch) => {
    pendingPatch.current = { ...(pendingPatch.current || {}), ...patch };
    // 'saving' is the legacy value; BoardPage's handleSaveStatus maps
    // it forward to 'typing'. Kept as-is so any other caller that
    // sends 'saving' still works.
    if (onSaveStatus) onSaveStatus('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, 4000);
  }, [flushSave, onSaveStatus]);


  // Walter 2026-08-24: ADOPT OUR OWN SORT LOCALLY. organize rewrites every
  // line and used to reach this panel the long way round — through Firestore
  // and back — so the buffer saw "every line moved" as a foreign change and
  // had to merge it against anything typed during the round-trip. That can't
  // merge cleanly, and it handed him a conflict band on a single device with
  // both ends online. Applying the result here means there is no divergence
  // to reconcile: the buffer and the base both become the sorted text, and
  // the server echo that follows lands as `keep`.
  const applyOrganizeResult = useCallback((newContent) => {
    if (typeof newContent !== 'string') return;
    // Drop any save still queued from before the sort. It holds PRE-sort
    // text, and letting it fire afterwards re-sends the old document over
    // the sorted one — the second half of the oscillation Walter's log
    // caught on 2026-08-24 (server size going 79 → 64 → 79 within a second).
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    pendingPatch.current = null;
    setLocalContent(newContent);
    localContentRef.current = newContent;
    setBase(newContent);
    const view = mdEditorRef.current?.view;
    if (view) {
      // CM6 owns its document, so setLocalContent alone wouldn't move it.
      const caret = Math.min(view.state.selection.main.head, newContent.length);
      dispatchDocReplace(view, newContent, caret, caret);
    }
  }, []);

  // Manual line highlights (Ctrl+H). Commits through the same path as a
  // keystroke so save / lock / selection stay consistent. Walter 2026-06-20.
  // Hoisted so the global header Format menu commits through the SAME path as
  // the Ctrl+H/Ctrl+Shift+H keystrokes (save / lock / selection-restore).
  const formatCommit = useCallback((next, selStart, selEnd) => {
    setLocalContent(next);
    const patch = { content: next };
    if (list?.seededFromTemplate) patch.seededFromTemplate = false;
    scheduleSave(patch);
    if (lockEnabled) { lock.touch(); markEditing(); }
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) { try { ta.setSelectionRange(selStart, selEnd); } catch { /* noop */ } }
    });
  }, [list?.seededFromTemplate, scheduleSave, lockEnabled, lock, markEditing, textareaRef]);

  // Format-menu commit for the CM6 (live-preview) editor — dispatch straight
  // to the view (see FocusedPanel's mirror + the #132/#136 rationale). Walter
  // 2026-07-16.
  const cmFormatCommit = useCallback((next, selStart, selEnd) => {
    const view = mdEditorRef.current?.view;
    if (!view) return;
    dispatchDocReplace(view, next, selStart, selEnd);
    if (lockEnabled) { lock.touch(); markEditing(); }
  }, [lockEnabled, lock, markEditing]);

  const highlight = useLineHighlight({
    textareaRef,
    getContent: () => localContent,
    isReminderList: isDesignatedReminderList(userPrefs, list?.id),
    readOnly: effectiveReadOnly,
    pushToast,
    commit: formatCommit,
  });

  // Walter 2026-05-15: register flushSave with the parent so the
  // SaveIndicator's "tap to sync now" gesture can flush every list
  // with pending changes — not just the focused one. Registers on
  // mount, unregisters on unmount.
  useEffect(() => {
    if (!registerPendingFlush) return undefined;
    return registerPendingFlush(flushSave);
  }, [registerPendingFlush, flushSave]);

  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'hidden') flushSave(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', flushSave);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', flushSave);
      flushSave();
    };
  }, [flushSave]);

  // Title editing.
  const enterTitleEdit = useCallback((e) => {
    e.stopPropagation();
    // Role gate — viewers can't enter title-edit mode.
    if (!roleCanEdit) return;
    // Offline gate — shared lists go read-only when offline. Don't
    // even let the title input flip to edit mode; the readOnly
    // attribute would block typing anyway, but blocking the mode
    // change avoids the confusing "I clicked, looks like I'm editing,
    // but nothing's happening" surface.
    if (offlineLockedShared) return;
    setEditingTitle(true);
    setTimeout(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }, 30);
  }, [roleCanEdit, offlineLockedShared]);

  const confirmTitleEdit = useCallback(() => {
    setEditingTitle(false);
    const val = localTitle.trim() || '';
    setLocalTitle(val);
    onUpdate(list.id, { title: val });
  }, [localTitle, list.id, onUpdate]);

  const onTitleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') { e.preventDefault(); confirmTitleEdit(); }
    if (e.key === 'Escape') { setEditingTitle(false); setLocalTitle(list.title || ''); }
  }, [confirmTitleEdit, list.title]);

  // Drag - reads position from DOM to avoid stale props.
  const onHeaderMouseDown = useCallback((e) => {
    if (editingTitle) return;
    if (e.target.tagName === 'BUTTON') return;
    if (e.detail === 2) return;
    e.preventDefault();
    onFocus(list.id);
    isDragging.current = false;
    const panelRect = panelRef.current.getBoundingClientRect();
    // Walter 2026-07-04: scroll-AWARE positioning. The tile's style.left/top
    // are content coords relative to its offset parent (the free-canvas
    // content div). We compute them from the cursor minus where inside the
    // tile it was grabbed, against the content div's LIVE rect — so recomputing
    // with the same cursor after an auto-scroll yields the correct new
    // position (the tile follows the cursor as the canvas scrolls under it).
    const contentEl = panelRef.current.parentElement;
    const grabX = e.clientX - panelRect.left;
    const grabY = e.clientY - panelRect.top;
    const startMx = e.clientX;
    const startMy = e.clientY;
    let lastX = e.clientX;
    let lastY = e.clientY;
    const positionFor = (cx, cy) => {
      const r = contentEl.getBoundingClientRect(); // live — reflects scroll
      return {
        left: Math.max(0, (cx - grabX) - r.left),
        top: Math.max(0, (cy - grabY) - r.top),
      };
    };
    const applyPos = (cx, cy) => {
      const p = positionFor(cx, cy);
      panelRef.current.style.left = p.left + 'px';
      panelRef.current.style.top = p.top + 'px';
    };
    // Auto-scroll the canvas when the cursor nears an edge; re-apply the
    // tile position each scroll step so it tracks the cursor.
    const scroller = createDragAutoScroll({
      getContainer: () => nearestScrollable(panelRef.current),
      onScroll: () => applyPos(lastX, lastY),
    });

    const onMove = (ev) => {
      lastX = ev.clientX;
      lastY = ev.clientY;
      if (Math.abs(ev.clientX - startMx) > 2 || Math.abs(ev.clientY - startMy) > 2) isDragging.current = true;
      scroller.update(ev.clientX, ev.clientY);
      applyPos(ev.clientX, ev.clientY);
    };
    const onUp = (ev) => {
      scroller.stop();
      if (isDragging.current) {
        const p = positionFor(ev.clientX, ev.clientY);
        onUpdate(list.id, { x: p.left, y: p.top });
      }
      dragStart.current = null;
      isDragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [editingTitle, list.id, onFocus, onUpdate]);

  // Resize - reads size from DOM.
  const onResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeStart.current = {
      mx: e.clientX, my: e.clientY,
      ow: panelRef.current.offsetWidth,
      oh: panelRef.current.offsetHeight,
    };

    const onMove = (ev) => {
      const nw = Math.max(MIN_W, resizeStart.current.ow + (ev.clientX - resizeStart.current.mx));
      const nh = Math.max(MIN_H, resizeStart.current.oh + (ev.clientY - resizeStart.current.my));
      panelRef.current.style.width = nw + 'px';
      panelRef.current.style.height = nh + 'px';
    };
    const onUp = (ev) => {
      onUpdate(list.id, {
        w: Math.max(MIN_W, resizeStart.current.ow + (ev.clientX - resizeStart.current.mx)),
        h: Math.max(MIN_H, resizeStart.current.oh + (ev.clientY - resizeStart.current.my)),
      });
      resizeStart.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [list.id, onUpdate]);

  const hasTitle = localTitle && localTitle.trim() !== '';
  // Walter 2026-05-06 round 8: default-pattern names ("list 1",
  // "list 2", …) are auto-generated placeholders, not user-chosen
  // titles — same affordance as an empty title (italic + dim +
  // single-click to edit). The old `(empty)` UX is preserved this
  // way; users can rename in one click instead of having to
  // double-click-and-then-rename.
  const isDefaultName = /^list\s+\d+$/i.test((localTitle || '').trim());
  // Walter 2026-05-11: title is owner-only for shared lists. Invitees
  // shouldn't be able to rename a list they don't own — title is
  // identity, and unexpected renames from co-editors would be
  // confusing for the owner. Content edits still sync to everyone;
  // only the title field is gated. When the owner's live title is
  // empty, fall back to the placement record's cached title (set at
  // place-time from the invite's targetTitle / live list title) so
  // the invitee sees something stable instead of "(empty)".
  const displayTitle = hasTitle
    ? localTitle
    : (isSharedWithMe ? (list._sharedFromCachedTitle || '') : '');
  const displayHasTitle = displayTitle.trim() !== '';
  const titleIsPlaceholder = !displayHasTitle || isDefaultName;
  // Only owner-side users can enter title edit. Even when the title
  // is a placeholder, isSharedWithMe locks rename out (matches the
  // semantic: title belongs to the owner).
  const titleEditable = !isSharedWithMe;
  const isArchived = list.state === 'archived';

  // In grid mode the tile fills its parent slot; in free mode it floats
  // at list.x/list.y with list.w/list.h. The two paths share everything
  // else about the outer wrapper — border, rounding, focus treatment.
  //
  // Walter 2026-05-24 (per Tahmid's QA): per-list color tag. When
  // `list.color` is set we paint a 3px accent on the left edge as an
  // inset box-shadow so it doesn't shift the content-box layout.
  // Composes with the existing focus ring + elevation shadow.
  const colorValue = getListColorValue(list?.color);
  // Walter 2026-06-16: per-list color now paints the ENTIRE 1px frame (all
  // sides, same thickness as the default border) rather than a 3px left
  // bar — so the tile reads as one cohesive colored card alongside its
  // tinted header + outline band, instead of a one-sided stripe in a
  // different colour from the rest of the frame. Focus still shows via the
  // ring shadow below, so a colored tile is still visibly focusable.
  const baseShadow = isFocused
    ? '0 0 0 1px var(--accent-dim), var(--shadow-tile-focused)'
    : 'var(--shadow-tile)';
  const composedShadow = baseShadow;
  // Walter 2026-07-03: a coloured tile's frame is a FAINT tint of its colour,
  // not the saturated hue — the saturated outline (accent ring below) is
  // reserved for the ACTIVE tile. Colour still shows via the header wash +
  // coloured title + this soft frame.
  // Walter 2026-07-04: "orange = active everywhere." An ACTIVE tile's border
  // is always the accent (matching the accent ring below) so a coloured
  // active tile no longer clashes a faint colour border against the orange
  // ring ("can't decide which colour it wants to be"). The colour still reads
  // via the header wash + coloured title; the faint colour frame returns the
  // moment the tile goes inactive.
  const frameBorderColor = isFocused
    ? 'var(--accent-dim)'
    : (colorValue
        ? (getListColorTint(list?.color, 0.45) || 'var(--border)')
        : (isArchived ? 'var(--text-dimmer)' : 'var(--border)'));
  const outerStyle = inGrid
    ? {
        position: 'absolute', inset: 0,
        background: getListColorBodyBg(list?.color) || 'var(--surface-note)',
        // Grid mode mirrors free mode's visual language now that tiles
        // have real gaps around them: every tile gets a subtle drop
        // shadow so it reads as a card on the canvas, and the focused
        // tile adds a 1px accent ring (mirroring free mode exactly).
        // The gap gives the drop shadow space to render without fighting
        // adjacent tiles.
        border: `1px solid ${frameBorderColor}`,
        borderRadius: 'var(--radius)',
        display: 'flex', flexDirection: 'column',
        boxShadow: composedShadow,
        overflow: 'hidden',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }
    : {
        position: 'absolute',
        left: list.x, top: list.y,
        width: list.w, height: list.h,
        zIndex: list.z || 10,
        background: getListColorBodyBg(list?.color) || 'var(--surface-note)',
        border: `1px solid ${frameBorderColor}`,
        borderRadius: 'var(--radius)',
        display: 'flex', flexDirection: 'column',
        boxShadow: composedShadow,
        overflow: 'hidden',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        minWidth: MIN_W, minHeight: MIN_H,
      };

  return (
    <div
      ref={panelRef}
      data-list-id={list.id}
      data-list-tile
      onMouseDown={() => onFocus(list.id)}
      style={outerStyle}
    >
      {/* Archived banner — visible only when the note is opened from the
          archive. Clicking "unarchive" flips state=open; otherwise the note
          stays archived even after closing the panel. */}
      {isArchived && (
        <div
          onMouseDown={e => e.stopPropagation()}
          style={{
            display: 'flex', alignItems: 'center',
            padding: '0 8px 0 10px', height: '24px',
            flexShrink: 0,
            background: 'var(--surface2)',
            borderBottom: '1px solid var(--border)',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            color: 'var(--text-dim)',
            gap: '6px', userSelect: 'none',
          }}
        >
          <span style={{ color: 'var(--text-dimmer)', letterSpacing: '0.5px' }}>ARCHIVED</span>
          <span style={{ flex: 1 }} />
          <button
            onMouseDown={e => e.stopPropagation()}
            onClick={() => onUnarchive?.(list.id)}
            style={{
              background: 'none',
              border: '1px solid var(--accent-dim)',
              color: 'var(--accent)',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              padding: '2px 8px',
              borderRadius: '2px',
              cursor: 'pointer',
              transition: 'background 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent)'; e.currentTarget.style.color = '#000'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--accent)'; }}
            title="move this note back to the open list"
          >
            unarchive
          </button>
        </div>
      )}

      {/* Header
          - Double-click anywhere on the header expands the tile into
            the focused view (matches finder/explorer conventions).
          - When the title is empty, a single click on the title slot
            enters rename mode — that's the only click-to-edit surface.
          - When the title already has content, no click-to-edit (use
            the ⋯ menu's "rename" entry to avoid accidental edits). */}
      <div
        className="tile-header"
        onMouseDown={
          inGrid
            ? (onGridHeaderMouseDown
                ? (e) => onGridHeaderMouseDown(list.id, e)
                : undefined)
            : onHeaderMouseDown
        }
        onDoubleClick={(e) => {
          // Ignore double-clicks that originated in the title <input>
          // (rename mode) or on buttons — those have their own handlers.
          if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON') return;
          expandWithCursor();
        }}
        style={{
          display: 'flex', alignItems: 'center',
          padding: '0 8px 0 10px', height: '34px',
          flexShrink: 0,
          cursor: editingTitle
            ? 'text'
            : (inGrid
                ? (onGridHeaderMouseDown ? 'grab' : 'default')
                : 'grab'),
          borderBottom: '1px solid var(--border)',
          // Walter 2026-06-13: per-list color now washes the header band
          // (soft tint over --surface2) instead of only a 3px left edge —
          // far more visible at a glance, body stays clean/readable.
          background: getListColorHeaderBg(list?.color) || 'var(--surface2)', gap: '6px',
          userSelect: 'none',
        }}
      >
        {editingTitle ? (
          <input
            ref={titleInputRef}
            value={localTitle}
            onChange={e => setLocalTitle(e.target.value)}
            onBlur={confirmTitleEdit}
            onKeyDown={onTitleKeyDown}
            onMouseDown={e => e.stopPropagation()}
            placeholder="list title..."
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: 'var(--accent)', fontFamily: 'var(--mono)',
              fontSize: 'calc(11px * var(--ui-scale, 1))', fontWeight: 600,
              padding: '2px 4px', borderRadius: '2px',
              borderBottom: '1px solid var(--accent-dim)', minWidth: 0,
            }}
            spellCheck={false}
          />
        ) : (
          <>
            {/* Drag affordance — always shown now (Walter 2026-05-06
                round 7). Previously hidden on titled tiles on the
                theory that the title was an implicit grab handle, but
                Walter found the affordance undiscoverable without an
                explicit visual cue. The grip is faint enough not to
                compete with the title text. */}
            <span className="tile-drag-grip"><DragGrip /></span>
            {/* hasTitle reference kept so any future read of the var
                still works — the grip render no longer gates on it. */}
            {/* Designated reminder list marker — small bell BEFORE the
                title with hover-explanation. Clarifies which list
                is the "every paragraph is a reminder" one without
                requiring users to dive into Settings. */}
            {isDesignatedReminderList(userPrefs, list.id) && (
              <span
                title="this list is being used for reminders — every line becomes one, and lines right under it are its notes"
                style={{
                  flex: 'none',
                  color: 'var(--accent)',
                  fontSize: 'calc(10px * var(--ui-scale, 1))',
                  marginRight: '2px',
                }}
              >
                🔔
              </span>
            )}
            {isDesignatedLogList(userPrefs, list.id) && (
              <span
                title="this list is a log — every new paragraph auto-stamps with today's date"
                style={{
                  flex: 'none',
                  color: 'var(--accent)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(11px * var(--ui-scale, 1))',
                  fontWeight: 700,
                  marginRight: '2px',
                  lineHeight: 1,
                }}
              >
                @
              </span>
            )}
            <span
              // Layout cell: flex:1 reserves the space + truncates, pushing
              // the badges/buttons to the right. It carries NO click handler
              // so clicking the empty area past the text falls through to the
              // header (drag/focus) — see the inner span for the click target.
              style={{
                flex: 1, fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))', fontWeight: 600,
                // Walter 2026-07-03: coloured list paints its title in its own
                // colour (matching the sidebar name), over the header wash.
                color: titleIsPlaceholder ? 'var(--text-dimmer)' : (getListColorTextValue(list?.color) || 'var(--text)'),
                fontStyle: titleIsPlaceholder ? 'italic' : 'normal',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {/* Walter 2026-07-04: single-click to rename, but the click
                  target is JUST the letters (inline span) — not the whole
                  cell — so clicking "way over in the empty area" no longer
                  drops into rename. stopPropagation keeps it off the drag. */}
              <span
                onClick={titleEditable ? enterTitleEdit : undefined}
                onMouseDown={titleEditable ? (e) => e.stopPropagation() : undefined}
                title={!titleEditable ? "owner controls this list's title" : 'click to rename'}
                style={{ cursor: titleEditable ? 'text' : 'inherit' }}
              >
                {displayHasTitle ? displayTitle : '(empty)'}
              </span>
            </span>
            {/* Shared-state badge — only renders when the list has any
                editors/viewers/shareToken. Click peeks the share via
                ShareModal. stopPropagation lives inside the badge so
                tile drag/click handlers don't fire. */}
            <ShareBadge
              editors={list.editors}
              viewers={list.viewers}
              shareToken={list.shareToken}
              // Walter 2026-05-12: also light up on tiles whose board
              // is shared even when the list itself has no per-list
              // ACL entries — stops the "I wrote a private note on a
              // shared board without realizing" confusion. Tooltip
              // shifts to "this list is on a shared board".
              boardEditors={boardEditors}
              boardViewers={boardViewers}
              onClick={() => onPeekShare?.(list)}
            />
            {/* Walter 2026-05-16: previous-versions icon. Renders only
                while there's an unresolved conflict — driven by the
                `conflictPendingReview` flag on the list doc (cleared
                when the user picks any resolution in the modal).
                The legacy `hasConflictMarker` check covers lists that
                still have the pre-2026-05-17 in-content marker; once
                those get resolved the marker is stripped too. */}
            {isConflictBandVisible({ flag: list.conflictPendingReview, dismissedKey: dismissedConflictKey, hasMarker: hasConflictMarker(localContent) }) && (
              <button
                onMouseDown={e => e.stopPropagation()}
                onClick={() => setShowHistory(true)}
                style={{
                  width: '20px', height: '20px', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  background: 'none', border: 'none',
                  color: 'var(--text-dimmer)', borderRadius: '2px',
                  padding: 0, flexShrink: 0, transition: 'color 0.15s',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dimmer)'}
                title={`${list.historyCount} previous version${list.historyCount === 1 ? '' : 's'} — click to view`}
                aria-label="previous versions"
              >
                {/* Counter-clockwise arrow ("undo" / "history"). */}
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 5.5a4 4 0 1 1 1.2 2.85"
                    stroke="currentColor" strokeWidth="1.3"
                    strokeLinecap="round" fill="none"
                  />
                  <path
                    d="M2 2.5v3h3"
                    stroke="currentColor" strokeWidth="1.3"
                    strokeLinecap="round" strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </button>
            )}
          </>
        )}

        {/* Organize-reminders button — Walter 2026-05-13. Shown
            whenever the list has at least one reminder doc pointing
            at it (covers designated lists as well as any list with
            ad-hoc 🔔 lines). One-shot rewrites the list content
            into ordered sections (Overdue / Current / Recurring /
            Undated / Done) with un-recognised lines collected under
            "Lines not processed" at the bottom. Pushes an undo
            toast so the previous content can be restored in one
            tap. */}
        {roleCanEdit && Array.isArray(reminders) && reminders.some(r => r.sourceListId === list.id) && onTriggerOrganize && (
          <button
            onMouseDown={e => e.stopPropagation()}
            onClick={async () => {
              // Walter 2026-06-16: make sort reliable even right after typing.
              // (2) FLUSHes the pending content write so organize re-reads the
              //     persisted, current content (avoids the organize↔capture
              //     write race that dropped a line from the list text).
              // (0) TREAT SORT AS AN IMPLICIT ENTER. Walter 2026-08-24: after
              //     the write race was closed, the one way left to lose a line
              //     was to type it and hit sort WITHOUT pressing Enter. Capture
              //     ran on `localContent` — React state, which lags the last
              //     keystroke, and in the CM6 editor lags the document by a
              //     render — so the in-progress line wasn't there to capture,
              //     wasn't in the pending save, and organize then read a server
              //     copy that had never heard of it. Take the text straight
              //     from the editor instead, and make it the pending write.
              const fresh = mdEditorRef.current?.view
                ? mdEditorRef.current.view.state.doc.toString()
                : localContent;
              if (fresh !== localContent) {
                setLocalContent(fresh);
                localContentRef.current = fresh;
                scheduleSave({ content: fresh });
              }
              // (1) CAPTURE any just-typed-but-uncaptured lines and AWAIT it,
              //     so the line you just typed is a real bell-line/reminder
              //     before organize reads it. Idempotent.
              try { await reminderCapture.captureAllUncaptured?.(fresh); } catch { /* non-fatal */ }
              // ...and let any debounced reminder reconcile land, so organize sorts
              // against docs that match the text it is about to read.
              try { await reminderCapture.flushReminderReconcile?.(); } catch { /* non-fatal */ }
              try { await flushSave(); } catch { /* non-fatal */ }
              // (3) Apply the sorted text to THIS buffer rather than waiting
              //     for it to arrive as a remote change — see
              //     applyOrganizeResult.
              const res = await onTriggerOrganize();
              if (res?.newContent != null) applyOrganizeResult(res.newContent);
            }}
            style={{
              width: '20px', height: '20px', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none',
              color: 'var(--text-dimmer)', borderRadius: '2px',
              padding: 0, flexShrink: 0, transition: 'color 0.15s', cursor: 'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dimmer)'}
            title="organise reminders into sections"
            aria-label="organise reminders"
          >
            {/* Sort-lines glyph — three horizontal bars of decreasing
                width with a small descending arrow. Reads as "sort
                by ordering" rather than "download". Walter 2026-05-13. */}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M1.5 2.5h7 M1.5 5.5h5 M1.5 8.5h3"
                stroke="currentColor" strokeWidth="1.3"
                strokeLinecap="round"
              />
              <path
                d="M9.5 3v6 M8 7.5l1.5 1.5 1.5-1.5"
                stroke="currentColor" strokeWidth="1.3"
                strokeLinecap="round" strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {/* Reset-size button — Walter 2026-05-21. Free-canvas only
            (grid tiles are slot-sized). When a tile has been resized
            larger than default, the bottom-right resize nub can end
            up below the viewport — a tall note in a short window —
            with no way to grab it. This snaps the tile back to the
            default dimensions, bringing the nub back on-screen.
            Shown ONLY when the tile is actually bigger than default,
            so default-sized tiles get no extra header clutter. */}
        {!inGrid && ((list.w || DEFAULT_W) > DEFAULT_W || (list.h || DEFAULT_H) > DEFAULT_H) && (
          <button
            onMouseDown={e => e.stopPropagation()}
            onClick={() => onUpdate?.(list.id, { w: DEFAULT_W, h: DEFAULT_H })}
            style={{
              width: '20px', height: '20px', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none',
              color: 'var(--text-dimmer)', borderRadius: '2px',
              padding: 0, flexShrink: 0, transition: 'color 0.15s', cursor: 'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dimmer)'}
            title="reset size"
            aria-label="reset tile size"
          >
            {/* Walter 2026-05-22: four diagonal arrows pointing
                inward from the corners toward the centre — the
                standard "compress / shrink to fit" glyph. Each arrow
                is a diagonal shaft into an L-shaped arrowhead at the
                inner tip. */}
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                d="M1 1L4 4 M4 2V4H2 M9 1L6 4 M6 2V4H8 M1 9L4 6 M4 8V6H2 M9 9L6 6 M6 8V6H8"
                stroke="currentColor" strokeWidth="1.3"
                strokeLinecap="round" strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        <button
          onMouseDown={e => e.stopPropagation()}
          onClick={expandWithCursor}
          style={{
            width: '20px', height: '20px', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none',
            color: 'var(--text-dimmer)', borderRadius: '2px',
            padding: 0, flexShrink: 0, transition: 'color 0.15s', cursor: 'pointer',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dimmer)'}
          title={`focused view (${SHORTCUT.expand})`}
        >
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
            {/* Diagonal outward arrows — expand (Steve 2026-07-11). */}
            <path
              d="M7 5 L11 1 M11 1 L8 1 M11 1 L11 4 M5 7 L1 11 M1 11 L4 11 M1 11 L1 8"
              stroke="currentColor" strokeWidth="1.3"
              strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* "+ new reminder" affordance moved to the agenda panel's
            header (Walter 2026-05-13). On a designated list, typing a
            new line already auto-creates a reminder via the capture
            flow, so a tile-header + button is redundant. */}

        {/* Overflow menu: archive / delete. Walter 2026-05-17 — menu
            body is now portalled via <PortalMenu> so it can't get
            clipped by the tile's overflow:hidden ancestor when the
            tile is near a viewport edge. */}
        <button
          ref={menuRef}
          onMouseDown={e => e.stopPropagation()}
          onClick={() => setMenuOpen(v => !v)}
          style={{
            width: '20px', height: '20px', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none',
            color: menuOpen ? 'var(--accent)' : 'var(--text-dimmer)', borderRadius: '2px',
            padding: 0, transition: 'color 0.15s', cursor: 'pointer',
            flexShrink: 0,
          }}
          onMouseEnter={e => { if (!menuOpen) e.currentTarget.style.color = 'var(--accent)'; }}
          onMouseLeave={e => { if (!menuOpen) e.currentTarget.style.color = 'var(--text-dimmer)'; }}
          title="more"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <circle cx="6" cy="2.5" r="1" fill="currentColor"/>
            <circle cx="6" cy="6" r="1" fill="currentColor"/>
            <circle cx="6" cy="9.5" r="1" fill="currentColor"/>
          </svg>
        </button>
        <PortalMenu
          open={menuOpen}
          triggerRef={menuRef}
          onClose={() => setMenuOpen(false)}
          minWidth={150}
          // Walter 2026-07-02: overflow visible so the colour + move-to
          // SubMenus can fly out to the side without the menu box clipping
          // them (the menu is short enough not to need scroll).
          overflow="visible"
        >
              <>
                  {/* Rename — Walter 2026-05-11: owner-only on shared
                      lists. Invitees see content sync but the title
                      stays the owner's. */}
                  {titleEditable && (
                    <MenuItem
                      onClick={(e) => { setMenuOpen(false); enterTitleEdit(e); }}
                    >
                      rename
                    </MenuItem>
                  )}
                  {isArchived ? (
                    <MenuItem
                      onClick={() => { setMenuOpen(false); onUnarchive?.(list.id); }}
                    >
                      unarchive
                    </MenuItem>
                  ) : (
                    <MenuItem
                      onClick={() => { setMenuOpen(false); onArchive?.(list.id); }}
                    >
                      archive
                    </MenuItem>
                  )}
                  {/* Designate / un-designate as reminder list. Walter
                      2026-05-06 round 8: reminders is now always-on,
                      so this menu item shows whenever we have the
                      prefs setter — no feature-flag gate. Toggling
                      here is the canonical way to manage the
                      designated set (the settings modal has no
                      picker). */}
                  {updateUserPrefs && (
                    <MenuItem
                      onClick={() => {
                        setMenuOpen(false);
                        const ids = Array.isArray(userPrefs.designatedReminderListIds)
                          ? userPrefs.designatedReminderListIds
                          : [];
                        const currentlyDesignated = isDesignatedReminderList(userPrefs, list.id);
                        const next = currentlyDesignated
                          ? ids.filter(id => id !== list.id)
                          : Array.from(new Set([...ids, list.id]));
                        updateUserPrefs({
                          designatedReminderListIds: next,
                          designatedReminderListId: null,
                          designatedReminderBoardId: null,
                        });
                        // Walter 2026-06-16: no longer seed demo content on
                        // designation. An empty designated reminder list now
                        // shows the how-it-works examples as a textarea
                        // PLACEHOLDER (getListEmptyHint) — which can never be
                        // captured as reminders. (The old seed-as-content +
                        // seededFromTemplate path captured the leftover
                        // example lines after the first edit.) Existing
                        // content is untouched; empty lists get the hint.
                      }}
                    >
                      {isDesignatedReminderList(userPrefs, list.id)
                        ? 'stop using for reminders'
                        : 'use this list for reminders'}
                    </MenuItem>
                  )}
                  {/* Log-list designation — parallel to reminder list.
                      A "log list" auto-prepends `@today` to every new
                      paragraph so users can journal (workouts, calls,
                      expenses) without manually dating each line. */}
                  {updateUserPrefs && (
                    <MenuItem
                      onClick={() => {
                        setMenuOpen(false);
                        const ids = Array.isArray(userPrefs.designatedLogListIds)
                          ? userPrefs.designatedLogListIds
                          : [];
                        const currentlyLog = isDesignatedLogList(userPrefs, list.id);
                        const next = currentlyLog
                          ? ids.filter(id => id !== list.id)
                          : Array.from(new Set([...ids, list.id]));
                        updateUserPrefs({ designatedLogListIds: next });
                        // Walter 2026-06-16: no demo-content seed on
                        // designation — an empty log list shows the examples
                        // as a placeholder (getListEmptyHint) that can't be
                        // @date-stamped. Existing content untouched.
                      }}
                    >
                      {isDesignatedLogList(userPrefs, list.id)
                        ? 'stop using as log'
                        : 'use this list as log'}
                    </MenuItem>
                  )}
                  {/* Share this list \u2014 Phase 2. Alpha-gated: parent
                      passes null for onShareList when sharing is off,
                      and we hide the menu item entirely. */}
                  {onShareList && (
                    <MenuItem onClick={() => { setMenuOpen(false); onShareList(list); }}>
                      share list
                    </MenuItem>
                  )}
                  {/* "place in board\u2026" \u2014 Walter 2026-05-11 sharing
                      refinements. Visible only when this list is shared
                      with the current user (i.e. they're not the owner).
                      Sets a sharedListPlacements doc so the list renders
                      inside one of THEIR own boards alongside their own
                      lists, instead of only in the "shared with me"
                      sidebar section. */}
                  {isSharedWithMe && onPlaceList && (
                    <MenuItem onClick={() => { setMenuOpen(false); onPlaceList(list); }}>
                      place in board{'\u2026'}
                    </MenuItem>
                  )}
                  {onMoveList && otherBoards.length > 0 && (
                    <SubMenu label="move to">
                      {otherBoards.map(b => (
                        <MenuItem
                          key={b.id}
                          onClick={() => { setMenuOpen(false); onMoveList?.(list.id, b.id); }}
                        >
                          {b.name}
                        </MenuItem>
                      ))}
                    </SubMenu>
                  )}
                  {/* Walter 2026-06-13: always-available access to version
                      history. The header clock icon only shows during an
                      ACTIVE conflict, so resolved-conflict snapshots became
                      unreachable even though they persist in lists/{id}/
                      history. Shown whenever the list has snapshots. */}
                  {list?.historyCount > 0 && (
                    <MenuItem onClick={() => { setMenuOpen(false); setShowHistory(true); }}>
                      previous versions
                    </MenuItem>
                  )}
                  {/* Color tag \u2014 Walter 2026-05-24 per Tahmid's QA
                      ("no visual differentiation between list types").
                      Opens a sub-menu of swatches; the chosen id is
                      written to list.color and rendered as an inset
                      left-edge accent on the tile (see outerStyle). */}
                  {/* Colour \u2014 folds out to the side on hover (tap on touch),
                      matching the sidebar list \u22ef. Walter 2026-07-02. */}
                  <SubMenu label="color">
                    <MenuItem
                      onClick={() => { setMenuOpen(false); onUpdate?.(list.id, { color: null }); }}
                    >
                      <span style={{
                        display: 'inline-block',
                        width: 10, height: 10,
                        borderRadius: '50%',
                        border: '1px solid var(--border-bright)',
                        marginRight: 8,
                        verticalAlign: 'middle',
                      }} />
                      none
                    </MenuItem>
                    {LIST_COLORS.map(c => (
                      <MenuItem
                        key={c.id}
                        onClick={() => { setMenuOpen(false); onUpdate?.(list.id, { color: c.id }); }}
                      >
                        <span style={{
                          display: 'inline-block',
                          width: 10, height: 10,
                          borderRadius: '50%',
                          background: c.value,
                          marginRight: 8,
                          verticalAlign: 'middle',
                        }} />
                        {c.label}
                      </MenuItem>
                    ))}
                  </SubMenu>
                  <MenuItem
                    onClick={() => { setMenuOpen(false); onDelete?.(list.id); }}
                    color="var(--danger)"
                  >
                    delete
                  </MenuItem>
                </>
        </PortalMenu>

        {/* Close button: sends the note to the sidebar as state=closed */}
        <button
          onMouseDown={e => e.stopPropagation()}
          onClick={() => onClose?.(list.id)}
          style={{
            width: '20px', height: '20px', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none',
            color: 'var(--text-dimmer)', borderRadius: '2px',
            padding: 0, flexShrink: 0, transition: 'color 0.15s', cursor: 'pointer',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dimmer)'}
          title="close (stays in sidebar)"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      {/* Shared-from band — Walter 2026-05-11. Sits directly under the
          header so the user can tell at a glance which tiles on their
          board belong to someone else. Renders only when the list is
          shared with the current user (placed-shared-list, or any
          list on a board they're a guest on). Owner name is best-
          effort from list._sharedFromOwnerName (set on the placement
          record at place-time); falls back to a generic "shared with
          you" label when unknown. */}
      {/* Walter 2026-05-26 band merge: when the list is shared with
          the current user AND has outside reminders, fold the muted-
          reminders note into SharedFromBand as a sub-line. When only
          one applies, the relevant band stands alone. countOutside
          is cheap (single regex pass over content) so computing it
          even when the shared band isn't showing is fine. */}
      {(() => {
        const outsideCount = countOutsideReminders(localContent, list?.id, reminders);
        if (isSharedWithMe) {
          return (
            <SharedFromBand
              ownerName={list._sharedFromOwnerName}
              role={roleCanEdit ? 'editor' : 'viewer'}
              mutedReminderNote={outsideCount > 0}
            />
          );
        }
        // Walter 2026-06-01: only show the "silent for you" muted band
        // to the OWNER when the list is actually shared with other
        // people — they're the only ones who could have reminders here
        // that aren't yours. On a private list every 🔔 line is the
        // owner's, so a non-zero outsideCount is just hash drift
        // (a recurring reminder's date prefix advanced, the line was
        // edited, or it's a multi-line reminder) and the band is a false
        // "these aren't yours." Suppress it there.
        const listSharedWithOthers = !!(
          (Array.isArray(list?.editors) && list.editors.length > 0) ||
          (Array.isArray(list?.viewers) && list.viewers.length > 0) ||
          list?.shareToken
        );
        if (outsideCount > 0 && listSharedWithOthers) {
          return (
            <MutedRemindersBand
              content={localContent}
              listId={list?.id}
              reminders={reminders}
            />
          );
        }
        return null;
      })()}

      {/* Designated-reminder-list counts band — shows total active
          reminders broken down by where they live. "Here" = how many
          have this list as their sourceListId (i.e. were created
          inside the dedicated reminder list directly). "Elsewhere"
          = reminders captured in other lists across any board. The
          breakdown is the user's quick "is my reminder list a true
          home, or are most reminders scattered" check. */}
      {isDesignatedReminderList(userPrefs, list.id) && Array.isArray(reminders) && (() => {
        const active = reminders.filter(r => r.status !== 'done');
        if (active.length === 0) return null;
        const here = active.filter(r => r.sourceListId === list.id).length;
        const elsewhere = active.length - here;
        // Walter 2026-05-06 round 5: clicking the band opens the
        // structured reminder view (agenda). Without onOpenAgenda
        // wired through (older callers, mobile fallback), the
        // band stays read-only as before.
        const clickable = typeof onOpenAgenda === 'function';
        return (
          <div
            onClick={clickable ? (e) => { e.stopPropagation(); onOpenAgenda(); } : undefined}
            title={clickable ? 'open all reminders' : 'click the 🔔 in the top header to view & manage all reminders'}
            style={{
              padding: '6px 12px',
              borderTop: '1px solid var(--border)',
              // Walter 2026-06-16: the reminder-summary band is neutral
              // chrome (like the header + outline band), so wash it with the
              // list color too — completing the "whole colored card" look.
              // (Semantic bands — conflict / organize / guest warnings —
              // keep their own colours so the meaning isn't lost.)
              background: getListColorHeaderBg(list?.color) || 'var(--surface2)',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              color: 'var(--text-dim)',
              display: 'flex', alignItems: 'center', gap: '6px',
              flexWrap: 'wrap',
              cursor: clickable ? 'pointer' : 'default',
              transition: 'color 0.12s, background 0.12s',
            }}
            onMouseEnter={clickable ? e => { e.currentTarget.style.color = 'var(--accent)'; } : undefined}
            onMouseLeave={clickable ? e => { e.currentTarget.style.color = 'var(--text-dim)'; } : undefined}
          >
            <span>{active.length} active, {here} here, {elsewhere} elsewhere</span>
            {/* Walter 2026-07-04: same right chevron as the outline/header band
                above, so it's clear this band is clickable (opens the agenda). */}
            {clickable && (
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true"
                style={{ marginLeft: 'auto', flexShrink: 0 }}>
                <path d="M2 1l3 3-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        );
      })()}

      {/* Outline band — lean by default, folds out on click.
          Walter's iteration 2026-05-05: the prior chip-strip ate
          a whole row + most chips truncated to nothing useful.
          New design:
            collapsed → single line "≡ N headers" (dim, clickable)
            expanded → headers listed below, each clickable to jump
          Auto-shows only when content has at least one `# header`.
          Walter 2026-07-04: rendered BELOW the reminder-count band —
          on a reminder list the "N active…" summary is the more
          important line, so it reads first. */}
      {(() => {
        const outline = parseOutline(localContent);
        const dateAnchors = parseDateAnchors(localContent);
        if (outline.length === 0 && dateAnchors.length === 0) return null;
        return (
          <OutlineBand
            outline={outline}
            dateAnchors={dateAnchors}
            textareaRef={textareaRef}
            content={localContent}
            colorBg={getListColorHeaderBg(list?.color)}
          />
        );
      })()}

      {/* Conflict band — shown when the list's content starts with
          the auto-merge marker (lib/mergeConflict.js). User clicked
          dismiss to clear the marker block once they've reviewed
          the prepended offline-only lines. Sits above the offline /
          viewer / lock badges so it's the first thing read when
          present. */}
      {isConflictBandVisible({ flag: list.conflictPendingReview, dismissedKey: dismissedConflictKey, hasMarker: hasConflictMarker(localContent) }) && (
        <div style={{
          padding: '8px 10px',
          // Walter 2026-05-11: quiet --border separator instead of the
          // brighter --accent-dim. Sitting directly under the header
          // (which has its own --border borderBottom), the orange
          // band-top read as a stray accent bar.
          borderTop: '1px solid var(--border)',
          background: 'var(--surface2)',
          // Walter 2026-05-17: stack text on top, button below on
          // narrow tile widths. The button gets pushed under instead
          // of squashing the text into a hard-to-read column.
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
        }}>
          <span style={{ color: 'var(--accent)', flexShrink: 0 }}>🔀</span>
          <span style={{ flex: '1 1 180px', minWidth: 0 }}>
            this list was edited in two places at once — review and resolve
          </span>
          <button
            onClick={() => setShowHistory(true)}
            disabled={effectiveReadOnly}
            title="open side-by-side view to choose how to resolve"
            style={{
              background: 'var(--accent)',
              color: 'var(--bg)',
              border: '1px solid var(--accent)',
              borderRadius: 'var(--radius)',
              padding: '3px 10px',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              cursor: effectiveReadOnly ? 'not-allowed' : 'pointer',
              opacity: effectiveReadOnly ? 0.5 : 1,
              fontWeight: 600,
            }}
          >
            resolve…
          </button>
        </div>
      )}

      {/* Offline banner — shared lists are read-only while the tab
          can't reach the server. Walter's decision: prefer "you
          can't edit right now" over the conflict-resolution mess
          of two users typing simultaneously while offline. Wins
          over the viewer / lock badges below by appearing first;
          the underlying state is moot until you're back online. */}
      {offlineLockedShared && (
        <div style={{
          padding: '6px 10px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface2)',
          display: 'flex', alignItems: 'center', gap: '8px',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
        }}>
          <span style={{ color: 'var(--accent)' }}>📶</span>
          <span style={{ flex: 1 }}>
            you're offline — shared lists are read-only until you reconnect
          </span>
        </div>
      )}

      {/* Viewer banner — shown when role doesn't permit writes (the
          user is a board-level or list-level viewer, not editor or
          owner). Replaces the silent "type but nothing saves" surface
          that confused early testers. Sits just above the textarea
          so it's the first thing read. Skips the lock badge below
          because viewers don't engage the edit-lock. */}
      {!offlineLockedShared && !roleCanEdit && lockEnabled && (
        <div style={{
          padding: '6px 10px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface2)',
          display: 'flex', alignItems: 'center', gap: '8px',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
        }}>
          <span style={{ color: 'var(--accent)' }}>👁</span>
          <span style={{ flex: 1 }}>
            view-only — only editors can change this list
          </span>
        </div>
      )}

      {/* Self-editing badge — shown when YOU hold the lock on a
          shared list and you're actively typing. Per Walter's
          2026-05-04 share-transparency request: gives you a clear
          "I'm holding this" indicator + a manual "done, release"
          escape hatch styled like the take-over button so a
          collaborator can grab it without waiting on the 30s idle
          timer. Skipped offline (no point releasing a lock no one
          can take while disconnected) and when soft-lock isn't
          engaged (own / non-shared list — no one's contending). */}
      {/* Walter 2026-05-16: "you are editing" band only on shared
          lists — for solo multi-device, holding the lock is
          implementation detail, not something the user needs to see. */}
      {!offlineLockedShared && roleCanEdit && hasSharedCollaborator && lock.heldByMe && (
        // Walter 2026-05-06 round 6: on narrow tiles "editing shared
        // list" was breaking across three lines as the row tried to
        // fit text + button. flexWrap lets the button drop to its
        // own line when there isn't room. Whitespace nowrap on the
        // label keeps "editing shared list" together rather than
        // breaking word-by-word. Button gets marginLeft auto so it
        // right-aligns whether on the same row or wrapped.
        // Walter 2026-05-11: quiet separator (see conflict band above).
        <div style={{
          padding: '6px 10px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface2)',
          display: 'flex', alignItems: 'center', gap: '8px',
          flexWrap: 'wrap',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
        }}>
          <span style={{ color: 'var(--accent)' }}>✏️</span>
          <span style={{ whiteSpace: 'nowrap' }}>editing shared list</span>
          <button
            onClick={() => lock.release()}
            title="release the lock so someone else can edit"
            style={{
              background: 'transparent',
              color: 'var(--accent)',
              border: '1px solid var(--accent-dim)',
              borderRadius: 'var(--radius)',
              padding: '3px 8px',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              cursor: 'pointer',
              marginLeft: 'auto',
              whiteSpace: 'nowrap',
            }}
          >
            done, release
          </button>
        </div>
      )}

      {/* Lock badge — shown when another user holds the edit lock. UI
          turns the textarea read-only and surfaces a small "Anna is
          editing" indicator with a take-over button. Only renders when
          the soft-lock is engaged (shared list + alpha on) AND the
          current user has write access (otherwise the viewer banner
          above is the right surface, not a take-over button). Skipped
          when offline — the offline banner above is the right primary
          surface; the lock state we'd show might be stale anyway. */}
      {!offlineLockedShared && roleCanEdit && lockEnabled && lock.heldBy && !lock.heldByMe && (
        <div style={{
          padding: '6px 10px',
          // Walter 2026-05-11: quiet separator (see conflict band above).
          borderTop: '1px solid var(--border)',
          background: 'var(--surface2)',
          display: 'flex', alignItems: 'center', gap: '8px',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
        }}>
          <span style={{ color: 'var(--accent)' }}>🔒</span>
          <span style={{ flex: 1 }}>
            {/* Walter 2026-05-16: distinguish "another user" (shared
                list case) from "your other device" (solo multi-device
                case). Both surface the take-over button. */}
            {lock.heldBy.isOtherDevice
              ? `your other device is ${lock.heldBy.activelyTyping ? 'typing' : 'editing'}`
              : `${lock.heldBy.name} ${lock.heldBy.activelyTyping ? 'is typing' : 'is editing'}`}
          </span>
          <button
            onClick={async () => {
              if (lock.heldBy?.activelyTyping) return;
              await lock.acquire({ force: true });
              // Walter 2026-05-16: force-sync localContent from the
              // freshest list.content the moment we take over. The
              // focused-textarea bail-out in the sync effect was the
              // root cause of "I took over but I see stale content"
              // — even with the loosened guard, fast take-over +
              // immediate focus could race against an in-flight
              // snapshot. Pulling list.content explicitly here is
              // belt-and-braces: take-over implies you want what the
              // other editor wrote, not whatever your local cache had.
              setLocalContent(list.content || '');
              textareaRef.current?.focus();
            }}
            disabled={lock.heldBy?.activelyTyping}
            className={takeoverFlash && !lock.heldBy?.activelyTyping ? 'takeover-flash-pulse' : undefined}
            title={lock.heldBy?.activelyTyping
              ? 'wait until they pause typing'
              : 'take over editing'}
            style={{
              background: 'transparent',
              color: lock.heldBy?.activelyTyping ? 'var(--text-dimmer)' : 'var(--accent)',
              border: '1px solid ' + (lock.heldBy?.activelyTyping ? 'var(--border)' : 'var(--accent-dim)'),
              borderRadius: 'var(--radius)',
              padding: '3px 8px',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              cursor: lock.heldBy?.activelyTyping ? 'not-allowed' : 'pointer',
              opacity: lock.heldBy?.activelyTyping ? 0.6 : 1,
            }}
          >
            take over
          </button>
        </div>
      )}

      {/* Demo-content band — Walter 2026-05-26 v2.
          Shown when the list was seeded from a board template (welcome
          flow's "organizer" GTD demo) and the user hasn't yet edited
          the prose. Three behaviours depending on which kind of seed
          list this is:
            • "General Tips" list (one per board, holds the welcome
              walkthrough content) → single action: "keep but close".
              Most users want to read the tips, not lose them, and not
              have them taking up canvas space forever. "keep but close"
              tucks the tile into the sidebar (state: 'closed') and
              clears the demo flag so a future re-open renders normal.
            • Any other demo-seeded list → two actions: "keep" (just
              dismisses the band + flag, content stays as-is so the user
              can use the example as a starting structure) and "✕ clear"
              (wipes content too). Typing anything also auto-clears the
              flag via the onChange handler, so dismissal is optional.
          Italic text in the band itself echoes the italic body styling,
          making the visual connection between "this is example content
          rendered specially" and "this band is the affordance for that
          treatment". Slightly larger button padding + bolder text than
          v1 because v1's buttons read as background chrome — users
          missed them. */}
      {/* Walter 2026-05-28: also gate on localContent === (list.content
          || '') — once the user has started typing in this list, treat
          that as an implicit "keep" and hide the band immediately,
          even though the saved seededFromTemplate flag hasn't cleared
          yet. Without this, the band stays visible during the 4-sec
          save debounce, and a user who typed something + then clicked
          ✕ clear would lose what they just typed (clear wipes ALL
          content, including the in-progress local state). */}
      {list?.seededFromTemplate && !effectiveReadOnly && localContent === (list?.content || '') && (() => {
        const isGeneralTips = (list?.title || '').trim().toLowerCase() === 'general tips';
        const labelText = 'example content + tips';
        const btnStyle = {
          background: 'transparent',
          // Walter 2026-06-26: lighter/brighter outline so they read as
          // buttons (--border was nearly invisible on the band).
          border: '1px solid var(--border-bright)',
          borderRadius: 'var(--radius)',
          padding: '3px 10px',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          fontWeight: 500,
          color: 'var(--text-dim)',
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'color 0.12s, border-color 0.12s',
        };
        const onEnter = (e) => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.borderColor = 'var(--accent-dim)'; };
        const onLeave = (e) => { e.currentTarget.style.color = 'var(--text-dim)'; e.currentTarget.style.borderColor = 'var(--border-bright)'; };
        return (
          <div
            style={{
              padding: '7px 12px',
              borderTop: '1px solid var(--border)',
              background: 'var(--surface2)',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              color: 'var(--text-dim)',
              display: 'flex',
              alignItems: 'center',
              // Walter 2026-06-07: wrap to two lines on narrow tiles
              // (small windows / many columns) instead of truncating the
              // label to "exampl…". The label keeps a min width so it
              // stays on the same row when there's room; when the buttons
              // no longer fit beside it, they drop to a second line and
              // the label shows in full.
              flexWrap: 'wrap',
              rowGap: '6px',
              columnGap: '8px',
              userSelect: 'none',
            }}
            title="this content was seeded by the demo template — pick how to handle it, or just start typing"
          >
            <span style={{
              flex: '1 1 130px',
              minWidth: '130px',
              whiteSpace: 'normal',
              fontStyle: 'italic',
            }}>
              {labelText}
            </span>
            {isGeneralTips ? (
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => {
                  // Walter 2026-05-26 — schedule + flush so the save
                  // bypasses the 4-second debounce. The dismiss is a
                  // one-shot user action, not part of a typing stream
                  // — should feel instant. Without flushSave(), the
                  // band took 4s to disappear after click (debounced
                  // save → snapshot listener → re-render).
                  scheduleSave({ state: 'closed', seededFromTemplate: false });
                  flushSave();
                }}
                style={btnStyle}
                onMouseEnter={onEnter}
                onMouseLeave={onLeave}
                title="close this tile (tucks it into the sidebar) — you can re-open it any time to read the tips"
              >
                keep but close
              </button>
            ) : (
              // Walter 2026-06-26: group keep + clear so they wrap to the
              // second line TOGETHER (as one flex unit) instead of keep
              // staying beside the label while clear drops down alone.
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    // Dismiss the band only — content stays, flag clears.
                    // schedule + flush so the band disappears + styling
                    // normalizes immediately, not 4 seconds later.
                    scheduleSave({ seededFromTemplate: false });
                    flushSave();
                  }}
                  style={btnStyle}
                  onMouseEnter={onEnter}
                  onMouseLeave={onLeave}
                  title="keep this example content as-is — just dismiss the band"
                >
                  keep
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    setLocalContent('');
                    // Same flush pattern — bypass the 4s debounce on
                    // the band-side dismiss/clear.
                    scheduleSave({ content: '', seededFromTemplate: false });
                    flushSave();
                  }}
                  style={btnStyle}
                  onMouseEnter={onEnter}
                  onMouseLeave={onLeave}
                  title="wipe the example content so you can start fresh"
                >
                  ✕ clear
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* Walter 2026-05-28 (#8b): inline alert for anonymous guests on
          a designated reminder list. Tells the user, on the surface
          where they're setting reminders, that reminders won't actually
          fire without an account. The dim body text below reinforces
          the message ambiently; the site-wide GuestUpgradeHint banner
          surfaces it proactively. The band is dismiss-once-by-action —
          there's no × dismiss because misunderstanding "my reminders
          aren't firing" is the exact bug we're preventing. Clicking
          "sign in" routes through onRequestSignIn (parent prop). */}
      {isGuest && isDesignatedReminderList(userPrefs, list?.id) && (
        <GuestReminderBand onSignIn={onRequestSignIn} />
      )}

      {/* Body — textarea over a syntax backdrop. The wrapper carries the
          flex:1 the textarea used to own; the textarea fills it (background
          stays transparent so the backdrop's subtle structure tints show
          through). The backdrop is pointer-events:none and behind the text. */}
      <div style={{ flex: 1, position: 'relative', minHeight: 0, display: 'flex' }}>
      {useLivePreview ? (
        /* Experimental CodeMirror-6 live-preview editor (Walter 2026-07-12).
           Same save path as the textarea; reminder creation on blur via
           captureAllUncaptured. The Format menu drives this editor too (Walter
           2026-07-16): onFocus registers a cmRef target whose commit
           dispatches to the view. See editorFlag.js / FocusedPanel mirror. */
        <div
          onMouseDown={e => {
            // Keep clicks inside the editor from starting a tile drag (the
            // textarea path stops propagation the same way).
            e.stopPropagation();
            if (!offlineLockedShared && roleCanEdit && lockEnabled && lock.heldBy && !lock.heldByMe) flashTakeover();
          }}
          style={{
          flex: 1, minWidth: 0, minHeight: 0, width: '100%', overflow: 'hidden',
          // Padding moved INTO the editor content (--md-editor-pad) so the
          // CM6 scrollbar hugs the tile edge like the textarea's did
          // (Walter 2026-07-19).
          padding: 0, '--md-editor-pad': '10px 12px', boxSizing: 'border-box',
          fontSize: 'var(--note-font-size, 12px)',
          lineHeight: 'var(--note-line-height, 1.5)',
          opacity: effectiveReadOnly ? 0.6 : 1,
        }}>
          <MarkdownEditor
            ref={mdEditorRef}
            listId={list.id}
            value={localContent}
            readOnly={effectiveReadOnly}
            captureLine={effectiveReadOnly ? undefined : reminderCapture.captureLineForCM6}
            onChange={(raw, meta) => {
              if (effectiveReadOnly) return;
              // #239 (Walter's rule): a change we did not originate — the panel
              // handed the editor text that arrived from the cloud. Show it,
              // don't save it back. See MarkdownEditor's remoteSync note.
              if (meta && meta.remote) return;
              const { content: next, blocked, justCrossedSoft } = applyListSizeGuard(localContent, raw);
              if (blocked && pushToast) {
                pushToast('at the 950k character limit — please archive and start fresh', null, { ttlMs: 5000 });
              } else if (justCrossedSoft && pushToast) {
                pushToast('list is getting long — consider archiving and starting fresh', null, { ttlMs: 5000 });
              }
              reminderCapture.onContentChanged?.(localContent, next);
              setLocalContent(next);
              const patch = { content: next };
              if (list?.seededFromTemplate) patch.seededFromTemplate = false;
              scheduleSave(patch);
              if (lockEnabled) lock.touch();
              if (lockEnabled) markEditing();
            }}
            onFocus={async () => {
              onFocus(list.id);
              // Register as the global Format menu's target (Walter 2026-07-16
              // — the menu drives CM6 too). No notifyContentChange: the CM6
              // onChange above already runs the reminder reconciler.
              setFormatTarget({
                listId: list.id,
                cmRef: mdEditorRef,
                commit: cmFormatCommit,
                isReminderList: isDesignatedReminderList(userPrefs, list?.id),
                readOnly: effectiveReadOnly,
                pushToast,
              });
              if (!lockEnabled) return;
              const snapshotAtFocus = list.content || '';
              const { acquired, contended } = await lock.acquire();
              if (acquired) return;
              if (!contended) return;
              setLocalContent(snapshotAtFocus);
              if (pendingPatch.current && pendingPatch.current.content !== undefined) pendingPatch.current = null;
              if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
              if (pushToast) pushToast('another device just took the lock — your last keystrokes weren\'t saved', null, { ttlMs: 4500 });
            }}
            onBlur={() => {
              blurFormatTarget(list.id); // greys the global Format menu
              if (!effectiveReadOnly) {
                const cur = mdEditorRef.current?.getValue?.() ?? localContent;
                reminderCapture.handleBlur?.({ target: { value: cur } });
                // Don't strand a debounced reminder reconcile on the way out.
                reminderCapture.flushReminderReconcile?.();
              }
              flushSave();
              if (lockEnabled && lock.heldByMe) lock.release();
            }}
            // Folding the phone keyboard doesn't blur — same capture pass as
            // blur so the reminder registers (Walter 2026-07-17; see
            // FocusedPanel mirror).
            onKeyboardHide={() => {
              if (effectiveReadOnly) return;
              const cur = mdEditorRef.current?.getValue?.() ?? localContent;
              reminderCapture.handleBlur?.({ target: { value: cur } });
            }}
          />
        </div>
      ) : (<>
      <SyntaxBackdrop content={localContent} textareaRef={textareaRef} isReminderList={isDesignatedReminderList(userPrefs, list?.id)} />
      <textarea
        ref={textareaRef}
        value={localContent}
        // Walter 2026-06-16: marks this as a target for the Ctrl+F find bar
        // (FindBar searches every rendered list-content textarea).
        data-find-target="1"
        // Walter 2026-06-16: reminder/log lists show their how-it-works
        // examples as a PLACEHOLDER (empty-state hint), not as seeded
        // content — so the instructions can never be captured as reminders
        // or @date-stamped. Empty for plain lists.
        placeholder={getListEmptyHint(userPrefs, list.id)}
        readOnly={effectiveReadOnly}
        onChange={e => {
          // Defense-in-depth: refuse keystrokes if we can't write
          // (readOnly catches it in normal browsers, but onChange
          // can still fire for programmatic value sets / paste).
          if (effectiveReadOnly) return;
          const raw = e.target.value;
          // Size guard: cap content at LIST_CHAR_HARD_LIMIT. Soft toast
          // fires when the user first crosses the soft threshold.
          const { content: next, blocked, justCrossedSoft } =
            applyListSizeGuard(localContent, raw);
          if (blocked && pushToast) {
            // Short enough to fit the toast row at typical widths.
            // Full number lives in the soft-warning copy and the docs.
            pushToast('at the 950k character limit — please archive and start fresh', null, { ttlMs: 5000 });
          } else if (justCrossedSoft && pushToast) {
            pushToast('list is getting long — consider archiving and starting fresh', null, { ttlMs: 5000 });
          }
          // Bell-removal scan: if the user removed a 🔔 from a line
          // (or deleted the whole line), delete the matching reminder
          // doc. Compares prev vs next snapshots; safe no-op if no
          // 🔔-prefixed lines were touched.
          reminderCapture.onContentChanged?.(localContent, next);
          setLocalContent(next);
          // Walter 2026-05-26 — auto-clear the demo-content flag on
          // first edit so the italic-dim styling normalizes. Only
          // bother including the field in the patch when the flag
          // is currently set; subsequent edits don't keep writing
          // `seededFromTemplate: false` for no reason.
          const patch = { content: next };
          if (list?.seededFromTemplate) patch.seededFromTemplate = false;
          scheduleSave(patch);
          // Lock heartbeat — record activity timestamp on every keystroke
          // so the heartbeat tick knows we're still alive.
          if (lockEnabled) lock.touch();
          // Sticky-edit signal: keep the "editing shared list" band
          // visible across snapshot lapses (mobile bg, network blip).
          if (lockEnabled) markEditing();
        }}
        onFocus={async () => {
          // Register as the global Format menu's target (runs regardless of
          // lock state). Greys the menu in when a list is focused.
          setFormatTarget({
            listId: list.id,
            textareaRef,
            commit: formatCommit,
            isReminderList: isDesignatedReminderList(userPrefs, list?.id),
            readOnly: effectiveReadOnly,
            pushToast,
            // Lets the Format reminder button add/remove a 🔔 and have the
            // reminder doc created/deleted to match (the plain commit path
            // doesn't run the capture diff).
            notifyContentChange: (prev, next) => { if (!effectiveReadOnly) reminderCapture.onContentChanged?.(prev, next); },
          });
          if (!lockEnabled) return;
          // Snapshot the content at focus time so we can roll back if
          // the lock is lost to a genuine takeover. The acquire is
          // async; meanwhile the user can type optimistically into
          // the textarea. If we lose the lock race to another device,
          // characters typed in that window would otherwise queue a
          // save with a stale base — overwriting the other device's
          // edits when the save flushes.
          //
          // acquire() returns { acquired, contended }. `contended` is
          // the ONLY signal that another device genuinely holds the
          // lock — it can only be true when the acquire transaction
          // reached the server and saw a fresh foreign lock. A false
          // `contended` means the transaction never completed
          // (offline, a network blip, a snapshot still warming up
          // right after load) — that is NOT a takeover, so we keep
          // the user's optimistic typing and stay silent. Walter
          // 2026-05-21: gating on `contended` killed a phantom
          // "another device took the lock" toast that fired on a
          // transient blip with no second device involved.
          const snapshotAtFocus = list.content || '';
          const { acquired, contended } = await lock.acquire();
          if (acquired) return;
          if (!contended) return; // transient failure — keep optimistic typing
          // Real contention → another device beat us. Wipe any
          // optimistic local typing and cancel the queued save.
          setLocalContent(snapshotAtFocus);
          if (pendingPatch.current && pendingPatch.current.content !== undefined) {
            pendingPatch.current = null;
          }
          if (saveTimer.current) {
            clearTimeout(saveTimer.current);
            saveTimer.current = null;
          }
          if (pushToast) {
            pushToast('another device just took the lock — your last keystrokes weren\'t saved', null, { ttlMs: 4500 });
          }
        }}
        onKeyDown={(e) => {
          // Ctrl+H manual highlight (and popup hotkeys). Returns true when it
          // handled the key, so we stop before reminderCapture.
          if (highlight.onKeyDown(e)) return;
          // Walter 2026-05-18: Alt+Shift+↑/↓ moves the current line
          // (or selected block of lines) up/down — mirrors Google
          // Docs. Runs BEFORE reminderCapture so Enter / etc. still
          // route through there normally.
          if (e.altKey && e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
            if (effectiveReadOnly) return;
            const ta = textareaRef.current;
            if (!ta) return;
            const fn = e.key === 'ArrowUp' ? moveLinesUp : moveLinesDown;
            const r = fn(localContent, ta.selectionStart, ta.selectionEnd);
            if (!r) { e.preventDefault(); return; } // no-op edge (top/bottom)
            e.preventDefault();
            setLocalContent(r.nextValue);
            scheduleSave({ content: r.nextValue });
            // Restore selection on the moved block after React renders.
            requestAnimationFrame(() => {
              const cur = textareaRef.current;
              if (cur) cur.setSelectionRange(r.nextSelStart, r.nextSelEnd);
            });
            if (lockEnabled) lock.touch();
            return;
          }
          reminderCapture.handleKeyDown(e);
        }}
        onBlur={(e) => {
          // Greys the global Format menu (Format buttons preventDefault their
          // mousedown, so clicking one does NOT blur — focus + selection stay).
          blurFormatTarget(list.id);
          // Walter 2026-06-09: capture any lines typed-but-not-Entered
          // when the user leaves the textarea (covers walking away AND
          // clicking organize, which blurs first). Skip when read-only so
          // a viewer of a shared list never mutates the owner's content.
          if (!effectiveReadOnly) {
            reminderCapture.handleBlur(e);
            // Don't strand a debounced reminder reconcile on the way out.
            reminderCapture.flushReminderReconcile?.();
          }
          flushSave();
          // Release the lock when the user clicks away. Walter's
          // 2026-05-04 update overrides the earlier "no release on
          // blur" decision — when you click out, you're done for the
          // moment, so the "editing shared list" band should
          // disappear and other users can take over without waiting
          // 30s for the idle timer. If a quick click-glance proves
          // too aggressive (rapid lock churn), we can add a short
          // grace period before release.
          if (lockEnabled && lock.heldByMe) lock.release();
        }}
        onMouseDown={e => {
          e.stopPropagation();
          onFocus(list.id);
          // Locked by someone else → a plain click can't edit. Pulse the
          // take-over button so the path forward is obvious (Walter 2026-07-04).
          if (!offlineLockedShared && roleCanEdit && lockEnabled && lock.heldBy && !lock.heldByMe) flashTakeover();
        }}
        style={{
          flex: 1, position: 'relative', zIndex: 1,
          background: 'none', border: 'none', outline: 'none',
          // Walter 2026-05-26 demo-content treatment: italic + dimmer
          // text color while seededFromTemplate is true. Reads as
          // "this is an example, not your real content yet." Once the
          // user starts editing, scheduleSave clears the flag and the
          // styling normalizes on the next snapshot — brief visual
          // lag is acceptable (a couple seconds at most).
          //
          // Walter 2026-05-28 — guest-reminder ambient hint: when the
          // current user is anonymous AND this is a designated
          // reminder list, the entire list body renders in dim text.
          // Reads as "these reminders aren't actually firing until you
          // sign in." Resolves the moment the user upgrades (anonymous
          // → linked) — no manual refresh needed because props update.
          color: list?.seededFromTemplate
            ? 'var(--text-dim)'
            : (isGuest && isDesignatedReminderList(userPrefs, list?.id))
              ? 'var(--text-dim)'
              : 'var(--text)',
          fontStyle: list?.seededFromTemplate ? 'italic' : 'normal',
          fontFamily: 'var(--mono)',
          fontSize: 'var(--note-font-size, 12px)', lineHeight: 'var(--note-line-height, 1.5)',
          padding: '10px 12px', resize: 'none',
          userSelect: 'text', cursor: effectiveReadOnly ? 'not-allowed' : 'text',
          // Dim the body when read-only (locked by other OR role
          // doesn't permit writes) so it reads as inactive.
          opacity: effectiveReadOnly ? 0.6 : 1,
        }}
        spellCheck={true}
      />
      {/* "Mark as done" veil — Walter 2026-06-23. A SECOND backdrop instance,
          rendered AFTER the textarea with a higher z-index, so its translucent
          rectangles sit IN FRONT and grey out done lines. pointer-events:none
          keeps typing/clicking flowing to the textarea underneath. */}
      <SyntaxBackdrop content={localContent} textareaRef={textareaRef} isReminderList={isDesignatedReminderList(userPrefs, list?.id)} variant="veil" />
      </>)}
      </div>
      {highlight.popup && (
        <HighlightPopup
          x={highlight.popup.x}
          y={highlight.popup.y}
          active={highlight.popup.active}
          fading={highlight.popup.fading}
        />
      )}

      {/* (Char count footer removed in the new design — content extends to
          the tile bottom with no metadata bar. Resize affordance still sits
          in the bottom-right corner in free mode.) */}

      {/* Resize handle — hidden in grid mode (tile dimensions are computed
          by the grid, not stored on the list doc). */}
      {!inGrid && (
        <div
          onMouseDown={onResizeMouseDown}
          style={{
            position: 'absolute', bottom: 0, right: 0,
            width: '16px', height: '16px', cursor: 'nwse-resize', zIndex: 2,
          }}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: 'absolute', bottom: 2, right: 2, opacity: 0.45 }}>
            <path d="M9 1L1 9M9 5L5 9" stroke="var(--text-dim)" strokeWidth="1.2" strokeLinecap="round"/>
          </svg>
        </div>
      )}

      {/* Walter 2026-05-16: previous-versions side-by-side modal.
          Position-fixed; rendered inside the tile so its lifecycle
          tracks the panel, but visually it covers the whole viewport.
          For placed shared lists, route to the source-owner's subtree
          (where the actual list + history live), not the local user's
          placement-doc subtree. */}
      <HistoryPanel
        open={showHistory}
        onClose={() => setShowHistory(false)}
        uid={list._sharedFromOwnerUid || boardOwnerUid || currentUid}
        boardId={list._sharedFromBoardId || currentBoardId}
        listId={list.id}
        listTitle={localTitle || list.title}
        currentContent={localContent}
        currentTitle={localTitle}
        actorUid={currentUid}
        pushToast={pushToast}
        sourceList={list}
        onResolved={() => setDismissedConflictKey(flagKey(list.conflictPendingReview))}
        onApplyContent={(newContent, newTitle) => {
          // Walter 2026-05-17: write through the same path as a normal
          // edit so the conflict-aware save + per-device echo all
          // behave consistently. Also flips local state so the user
          // sees the change immediately.
          if (typeof newContent === 'string') {
            setLocalContent(newContent);
          }
          if (typeof newTitle === 'string') {
            setLocalTitle(newTitle);
          }
          const patch = {};
          if (typeof newContent === 'string') patch.content = newContent;
          if (typeof newTitle === 'string') patch.title = newTitle;
          if (Object.keys(patch).length > 0) scheduleSave(patch);
        }}
      />
    </div>
  );
}

// SharedFromBand — small inline band that sits below the header bar
// on a shared list's tile, identifying the owner. Walter 2026-05-11:
// added in response to "we should probably indicate the owner of
// these 'foreign lists' in a band on top of the list below the
// header bar" — without it, placed shared lists looked identical to
// own lists on the canvas. Mono-warm aesthetic: dim text on the
// same surface2 stripe pattern used by the outline / reminder count
// bands. Exported so FocusedPanel can re-render the same band.
export function SharedFromBand({ ownerName, role, mutedReminderNote = false }) {
  const name = (ownerName || '').trim();
  // Walter 2026-05-26: when this list is shared AND has reminders from
  // someone else, fold both signals onto a single row separated by a
  // mid-dot. Earlier two-row variant looked like two distinct bands
  // even though it was one element. Single-row reads as one piece of
  // chrome and saves vertical space on the tile. Both pieces use the
  // band's default --text-dim — no per-segment color overrides (the
  // grayscale/opacity treatment on the 🔕 glyph alone carries the
  // "muted" signal; differentiating the text shade just looked uneven).
  // The muted text segment carries the flex:1 + ellipsis when present,
  // so on narrow tiles it truncates first while the share label
  // stays readable. When the muted segment isn't there, a flex:1
  // spacer pushes the role badge to the right.
  return (
    <div
      style={{
        padding: '6px 12px',
        borderTop: '1px solid var(--border)',
        background: 'var(--surface2)',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(10px * var(--ui-scale, 1))',
        color: 'var(--text-dim)',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        userSelect: 'none',
      }}
      title={
        name
          ? (mutedReminderNote
              ? `shared by ${name} — reminders here are silent for you`
              : `shared by ${name}`)
          : (mutedReminderNote
              ? 'this list is shared with you — reminders here are silent for you'
              : 'this list is shared with you')
      }
    >
      <SharedGlyph />
      <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
        {name
          ? <>shared by <span style={{ color: 'var(--text)' }}>{name}</span></>
          : 'shared with you'}
      </span>
      {mutedReminderNote ? (
        <>
          <span aria-hidden="true" style={{ flexShrink: 0, opacity: 0.6 }}>·</span>
          <span aria-hidden="true" style={{ filter: 'grayscale(1)', opacity: 0.8, flexShrink: 0 }}>🔕</span>
          <span style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}>
            reminders here are silent for you
          </span>
        </>
      ) : (
        // No muted segment — empty spacer pushes the role badge to
        // the right edge (matches the pre-merge layout exactly).
        <span style={{ flex: 1 }} />
      )}
      {role === 'viewer' && (
        <span style={{
          flexShrink: 0,
          fontSize: 'calc(8px * var(--ui-scale, 1))',
          color: 'var(--text-dimmer)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '0 4px',
          letterSpacing: '0.4px',
          textTransform: 'uppercase',
        }}>
          read only
        </span>
      )}
    </div>
  );
}

/**
 * MutedRemindersBand — surfaces "there are 🔔 lines in this list that
 * aren't yours, and they won't ping you" without making the user hunt
 * for the distinction line-by-line.
 *
 * Personal reminders model (Walter 2026-05-25): every 🔔 line in
 * listicity is owned by the user who typed it; alarms only fire for
 * that owner. On shared lists, that means collaborators see the 🔔
 * character in the list text but never hear from it. This band tells
 * them so explicitly — front-and-center "silent for you" framing
 * matches Walter's ask ("more important than saying who set them is
 * telling the viewer it won't fire").
 *
 * Implementation note — Personal-model visibility constraint:
 * because we never write other users' UIDs into a reminder's aclUids
 * array, this client genuinely cannot read those other reminder docs
 * (Firestore rules deny). So we can't enumerate "outside reminders"
 * via the useReminders subscription. Instead we count 🔔-prefixed
 * lines in the list's CONTENT (the shared text is readable) and
 * subtract the lines whose sourceLineHash matches one of MY reminder
 * docs for this list. The remainder are reminders someone else set.
 *
 * If a 🔔 line is in the text but ITS doc was deleted (rare: user
 * killed their reminder via the agenda without saving the source
 * list back), it shows up as "outside" until the bell-removal scan
 * tidies the text — accept as edge case.
 */
export function countOutsideReminders(content, listId, myReminders) {
  if (!content || !listId) return 0;
  const myHashes = new Set(
    (myReminders || [])
      .filter(r => r.sourceListId === listId)
      .map(r => r.sourceLineHash)
  );
  let count = 0;
  const lines = content.split('\n');
  for (const line of lines) {
    // Mirror BELL_RE from useReminderCapture: 🔔 with optional ↻ and
    // optional trailing whitespace. Skip [DONE] / 🔕 / ~~🔔 — those
    // are completed reminders and won't fire for anyone anyway.
    const m = line.match(/^🔔↻?\s?/);
    if (!m) continue;
    const post = line.slice(m[0].length);
    // Inline FNV-1a 32-bit hash matching hashReminderLine in
    // useReminders.js. Inlined to avoid an extra import — this band
    // is the only consumer of the hash logic outside the hooks layer.
    let h = 0x811c9dc5;
    const s = (post || '').trim();
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    const hash = (h >>> 0).toString(16).padStart(8, '0');
    if (!myHashes.has(hash)) count++;
  }
  return count;
}

export function MutedRemindersBand({ content, listId, reminders }) {
  const count = countOutsideReminders(content, listId, reminders);
  if (count <= 0) return null;
  // Walter 2026-05-25: dropped the leading count. The number isn't
  // actionable — the user can already see how many bell lines there
  // are by scanning the textarea — and the phrase reads more
  // naturally as a behaviour statement than a tally.
  const label = 'reminders here are silent for you';
  return (
    <div
      style={{
        padding: '6px 12px',
        borderTop: '1px solid var(--border)',
        background: 'var(--surface2)',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(10px * var(--ui-scale, 1))',
        color: 'var(--text-dim)',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        userSelect: 'none',
      }}
      title="these reminders belong to other collaborators on this list — they won't fire for you. add your own 🔔 line if you want a reminder for yourself."
      aria-label={label}
    >
      {/* 🔕 (bell-with-cancellation-stroke) carries the "muted" signal
          visually. CSS grayscale + slight desat keeps it from
          competing with active 🔔 emoji in the textarea body. */}
      <span aria-hidden="true" style={{ filter: 'grayscale(1)', opacity: 0.8, flexShrink: 0 }}>🔕</span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </div>
  );
}

// Tiny "shared" glyph (two overlapping cards) used by SharedFromBand
// and matching the visual vocabulary of the sidebar ShareBadge.
function SharedGlyph() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 11 11"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0, opacity: 0.85 }}
    >
      <rect x="0.5" y="3.5" width="7" height="7" rx="1.2"
        stroke="currentColor" strokeWidth="1" fill="none" />
      <rect x="3.5" y="0.5" width="7" height="7" rx="1.2"
        stroke="currentColor" strokeWidth="1" fill="var(--surface2)" />
    </svg>
  );
}

// OutlineBand — lean fold-out outline indicator. When closed shows
// a one-line "≡ N headers · M dates" trigger; click expands a
// vertical list of clickable anchors (caret jumps to that line).
// Click trigger again to collapse. Walter's preferred design over
// the previous chip strip: most users have a couple of headers and
// don't want the strip eating space; the few who use lots of headers
// — or, since 2026-05-10, lots of `@<date>` log entries — can fold
// it out only when navigating. Same component is used by FocusedPanel
// (mobile + desktop) so the affordance is consistent app-wide.
export function OutlineBand({ outline, dateAnchors = [], textareaRef, content, colorBg = null }) {
  const [open, setOpen] = useState(false);
  const merged = mergeOutlineAndDates(outline, dateAnchors);
  if (merged.length === 0) return null;
  const headerCount = (outline || []).length;
  const dateCount = (dateAnchors || []).length;
  const summary = dateCount === 0
    ? `${headerCount} header${headerCount === 1 ? '' : 's'}`
    : headerCount === 0
      ? `${dateCount} date${dateCount === 1 ? '' : 's'}`
      : `${headerCount} header${headerCount === 1 ? '' : 's'} · ${dateCount} date${dateCount === 1 ? '' : 's'}`;
  return (
    <div style={{
      borderBottom: '1px solid var(--border)',
      // surface2 (one shade lighter than the tile body's surface) so
      // the band visually separates from the prose below it.
      // Walter's round-7 nit: matches the tile-header tone, reads as
      // chrome rather than content.
      // Walter 2026-06-16: when the list has a color, wash the band with
      // the same tint as the header so the colored card reads coherently
      // (header + band + frame all the same hue) — was the last surface
      // still showing the default color.
      background: colorBg || 'var(--surface2)',
      flexShrink: 0,
    }}>
      <button
        type="button"
        onMouseDown={e => e.stopPropagation()}
        onClick={() => setOpen(v => !v)}
        title={open ? 'hide outline' : 'show outline'}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          width: '100%',
          padding: '4px 12px',
          background: 'transparent',
          border: 'none',
          // Walter 2026-07-04: match the reminder-summary band's tone so the
          // two chrome bands read as one family (was text-dimmer @ 0.65,
          // noticeably lighter than that band). Both now rest at --text-dim
          // (the darker grey) and brighten to accent on hover.
          color: 'var(--text-dim)',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'color 0.12s',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-dim)'; }}
      >
        {/* Three-bar outline glyph: identifies "this is a TOC". */}
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" aria-hidden="true">
          <path d="M2 2.8h7M3.5 5.5h5.5M2 8.2h7" />
        </svg>
        <span>{summary}</span>
        {/* Chevron rotates 90° on open so the affordance reads as
            "click to fold open / closed" without ambiguity. */}
        <svg
          width="8" height="8" viewBox="0 0 8 8" fill="none"
          aria-hidden="true"
          style={{
            marginLeft: 'auto',
            transition: 'transform 0.12s',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        >
          <path d="M2 1l3 3-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div style={{
          padding: '2px 8px 6px',
          maxHeight: 200,
          overflowY: 'auto',
        }}>
          {merged.map((entry, i) => (
            <OutlineBandRow
              key={i}
              entry={entry}
              onClick={() => {
                jumpToOffset(textareaRef.current, entry.charOffset, content);
                setOpen(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Renders one row of the OutlineBand, branching visual treatment by
// entry.kind ('header' vs 'date'). Headers preserve the existing
// indent-by-level look. Dates get a compact `@` prefix in the accent
// color so the dated rows are visually distinct from headers without
// requiring a second column or separator.
function OutlineBandRow({ entry, onClick }) {
  const isHeader = entry.kind === 'header';
  const label = isHeader ? entry.text : formatDateAnchorLabel(entry);
  const baseColor = isHeader
    ? (entry.level === 1 ? 'var(--text)' : 'var(--text-dim)')
    : 'var(--text-dim)';
  return (
    <button
      type="button"
      onMouseDown={e => e.stopPropagation()}
      onClick={onClick}
      title={label}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: '6px',
        width: '100%',
        textAlign: 'left',
        padding: isHeader
          ? `2px 8px 2px ${4 + (entry.level - 1) * 12}px`
          : '2px 8px',
        background: 'transparent',
        border: 'none',
        color: baseColor,
        fontFamily: 'var(--mono)',
        fontSize: isHeader && entry.level === 1
          ? 'calc(11px * var(--ui-scale, 1))'
          : 'calc(10px * var(--ui-scale, 1))',
        fontWeight: isHeader && entry.level === 1 ? 600 : 400,
        cursor: 'pointer',
        borderRadius: 'var(--radius)',
        transition: 'background 0.12s, color 0.12s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = 'var(--surface2)';
        e.currentTarget.style.color = 'var(--accent)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = baseColor;
      }}
    >
      {!isHeader && (
        <span style={{
          color: 'var(--accent)',
          flexShrink: 0,
          fontWeight: 600,
        }}>@</span>
      )}
      <span style={{
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
    </button>
  );
}

// Small menu item used inside the overflow popover.
function MenuItem({ children, onClick, color }) {
  return (
    <div
      onMouseDown={e => e.stopPropagation()}
      onClick={onClick}
      style={{
        padding: '8px 14px', cursor: 'pointer',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(11px * var(--ui-scale, 1))',
        color: color || 'var(--text-dim)',
        transition: 'background 0.1s, color 0.1s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface2)'; if (!color) e.currentTarget.style.color = 'var(--text)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; if (!color) e.currentTarget.style.color = 'var(--text-dim)'; }}
    >
      {children}
    </div>
  );
}

// Four-way move glyph — universally read as "drag me anywhere".
// Walter 2026-05-06 round 8: restored after a brief detour through
// six-dot grips; users were already familiar with the cross/arrow
// pattern from earlier rounds, the dot grip read as decoration
// rather than an affordance. Dim text color so it sits as a hint,
// not a competing element next to the title.
function DragGrip() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        flexShrink: 0,
        color: 'var(--text-dim)',
        cursor: 'grab',
      }}
    >
      <line x1="7" y1="3" x2="7" y2="11" />
      <line x1="3" y1="7" x2="11" y2="7" />
      <path d="M5 4 L7 2 L9 4" />
      <path d="M5 10 L7 12 L9 10" />
      <path d="M4 5 L2 7 L4 9" />
      <path d="M10 5 L12 7 L10 9" />
    </svg>
  );
}
